import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database } from 'lucide-react';
import api from '../api/api';
import { getPermissions } from '../api/configHelper';

const ITEMS_PER_PAGE = 10;
const MAIL_FONT_FAMILIES = ["Arial", "Calibri", "Times New Roman", "Georgia", "Verdana", "Tahoma", "Courier New"];
const MAIL_FONT_SIZES = [
  { label: "10", value: "1" },
  { label: "12", value: "2" },
  { label: "14", value: "3" },
  { label: "18", value: "4" },
  { label: "24", value: "5" },
  { label: "32", value: "6" },
  { label: "48", value: "7" }
];
const MAIL_TEXT_COLORS = ["#0f172a", "#1d4ed8", "#047857", "#b91c1c", "#c2410c", "#7c3aed", "#000000"];
const MAIL_HIGHLIGHT_COLORS = ["#ffffff", "#fef08a", "#bfdbfe", "#bbf7d0", "#fecaca", "#e9d5ff", "#fed7aa"];
const TICKET_MODULE_ID = "TicketingSystem";
const COMPANY_UPDATE_MODULE_ID = "UpdateCompanyDts";
const PROPERTY_SETUP_MODULE_ID = "PropertySetup";

const highlightDropdownText = (text, query) => {
  const value = String(text || "");
  const search = String(query || "").trim();
  if (!search) return value;

  const index = value.toLowerCase().indexOf(search.toLowerCase());
  if (index === -1) return value;

  return (
    <>
      {value.slice(0, index)}
      <span className="font-semibold text-blue-700">{value.slice(index, index + search.length)}</span>
      {value.slice(index + search.length)}
    </>
  );
};

const CustomDropdown = ({
  options = [],
  valueKey,
  labelKey,
  value,
  onChange,
  placeholder = "Select...",
  disabled = false,
  openDirection = "down"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const selected = options.find(
      (option) => option?.[valueKey]?.toString() === value?.toString()
    );
    setInputValue(selected ? selected[labelKey] : "");
  }, [value, options, valueKey, labelKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    String(option?.[labelKey] || "").toLowerCase().includes(inputValue.toLowerCase())
  );

  useEffect(() => {
    if (listRef.current && activeIndex >= 0) {
      listRef.current.children[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const selectOption = (option) => {
    onChange(option[valueKey]);
    setInputValue(option[labelKey]);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      setIsOpen(true);
      setActiveIndex(0);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (index < filteredOptions.length - 1 ? index + 1 : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => (index > 0 ? index - 1 : filteredOptions.length - 1));
    } else if (event.key === "Enter" && isOpen && activeIndex >= 0) {
      event.preventDefault();
      const selected = filteredOptions[activeIndex];
      if (selected) selectOption(selected);
    } else if (event.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <input
        type="text"
        className="w-full h-10 px-3 py-2 text-sm font-semibold transition bg-white border rounded-lg outline-none border-slate-300 pr-9 text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
        placeholder={placeholder}
        value={inputValue}
        disabled={disabled}
        onFocus={() => !disabled && setIsOpen(true)}
        onChange={(event) => {
          setInputValue(event.target.value);
          setIsOpen(true);
          setActiveIndex(-1);
        }}
        onKeyDown={handleKeyDown}
      />
      <span className="absolute -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400">
        ▾
      </span>

      {isOpen && !disabled && (
        <div
          ref={listRef}
          className={`absolute z-[9999] max-h-52 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl ${
            openDirection === "up" ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => (
              <div
                key={option[valueKey]}
                className={`cursor-pointer px-3 py-2 text-sm text-slate-700 ${
                  index === activeIndex ? "bg-blue-50 font-semibold" : "hover:bg-slate-50"
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={() => selectOption(option)}
              >
                {highlightDropdownText(option[labelKey], inputValue)}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-400">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Header({ onToggleSidebar, headerActions }) {
  const [companyDetails, setCompanyDetails] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scrolled, setScrolled] = useState(false);
  const [userName, setUserName] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [emailId, setEmailId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState(null);
  const [resetStep, setResetStep] = useState(1); // 1: enter details, 2: enter OTP
  const [otp, setOtp] = useState("");
  const [permissions, setPermissions] = useState({});
  const [ticketMenu, setTicketMenu] = useState(null);
  const [companyUpdateMenu, setCompanyUpdateMenu] = useState(null);
  const [propertySetupMenu, setPropertySetupMenu] = useState(null);
  const popupRef = useRef();
  const [taskCount, setTaskCount] = useState(0);
  const [taskList, setTaskList] = useState([]);
  const [showNotice, setShowNotice] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [taskStatusOptions, setTaskStatusOptions] = useState([]);
  const [taskLogs, setTaskLogs] = useState([]);
  const [showTaskLogForm, setShowTaskLogForm] = useState(false);
  const [taskLogNotes, setTaskLogNotes] = useState("");
  const [taskLogStatus, setTaskLogStatus] = useState("");
  const [taskLogSaving, setTaskLogSaving] = useState(false);
  const [taskLogDeletingId, setTaskLogDeletingId] = useState(null);
  const [taskLogMessage, setTaskLogMessage] = useState(null);
  const [noticeSelectedDate, setNoticeSelectedDate] = useState(new Date());
  const [noticeMonth, setNoticeMonth] = useState(new Date());
  const [noticeFromDate, setNoticeFromDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [noticeToDate, setNoticeToDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
  });
  const [appliedNoticeFromDate, setAppliedNoticeFromDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [appliedNoticeToDate, setAppliedNoticeToDate] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isMailOpen, setIsMailOpen] = useState(false);
  const [selectedMail, setSelectedMail] = useState(null);
  const [mailSearch, setMailSearch] = useState("");
  const [mailFolder, setMailFolder] = useState("outbox");
  const [mailItems, setMailItems] = useState([]);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailError, setMailError] = useState("");
  const [mailSending, setMailSending] = useState(false);
  const [mailSendingAll, setMailSendingAll] = useState(false);
  const [mailUpdating, setMailUpdating] = useState(false);
  const [mailLinkInserting, setMailLinkInserting] = useState(false);
  const [mailDeletingLogId, setMailDeletingLogId] = useState(null);
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupResult, setBackupResult] = useState(null);
  const [vendorToLoading, setVendorToLoading] = useState(false);
  const [mailSendMessage, setMailSendMessage] = useState(null);
  const [mailPopupSize, setMailPopupSize] = useState("normal");
  const [isMailContentEditing, setIsMailContentEditing] = useState(false);
  const [isMailToEditing, setIsMailToEditing] = useState(false);
  const [isMailListCollapsed, setIsMailListCollapsed] = useState(false);
  const [mailPopupOffset, setMailPopupOffset] = useState({ x: 0, y: 0 });
  const [isMailDragging, setIsMailDragging] = useState(false);
  const mailBodyEditorRef = useRef(null);
  const mailBodyLoadedIdRef = useRef(null);
  const mailSelectionRef = useRef(null);
  const mailDragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const mailSendAllStopRef = useRef(false);
  const taskLogOpenTaskRef = useRef(null);

  const [taskLinks, setTaskLinks] = useState([]);
  const [approvalCount, setApprovalCount] = useState(0);
  const [noticeSearch, setNoticeSearch] = useState("");
  const navigate = useNavigate();
const [showLogoutMailConfirm, setShowLogoutMailConfirm] = useState(false);
const [logoutOutboxCount, setLogoutOutboxCount] = useState(0);
const AUTH_STORAGE_KEYS = ["AccessToken", "RefreshToken", "CurrentAuthToken", "AuthToken", "userId", "userName", "UserInfo", "LogId"];

  useEffect(() => {
  loadTasks();
  loadApprovalCount();
}, []);

useEffect(() => {
  if (!showNotice) return undefined;

  const handleNoticeEscape = (event) => {
    if (event.key === "Escape") {
      if (openTaskId) {
        setOpenTaskId(null);
        return;
      }

      setShowNotice(false);
    }
  };

  window.addEventListener("keydown", handleNoticeEscape);
  return () => window.removeEventListener("keydown", handleNoticeEscape);
}, [showNotice, openTaskId]);


const navigateToApproval = () => {
  const allMenus = JSON.parse(sessionStorage.getItem("allSubMenuList"));

  const list = allMenus?.Table || [];

  // 🔍 find Approval module
  const approvalMenu = list.find(
    (x) => x.MdlId === "Approval" || x.MdlDisplayName === "Approval"
  );

  if (approvalMenu?.ReactWebPath) {
    let url = `${approvalMenu.ReactWebPath}?mdlId=${approvalMenu.MdlId}`;

    if (approvalMenu.TxnType) {
      url += `&TxnType=${encodeURIComponent(approvalMenu.TxnType)}`;
    }

    if (approvalMenu.TxnSubType) {
      url += `&TxnSubType=${encodeURIComponent(approvalMenu.TxnSubType)}`;
    }

    window.location.href = url;
  } else {
    console.warn("Approval menu not found");
  }
};

const buildMenuUrl = (menu) => {
  if (!menu?.ReactWebPath) return "";

  const cleanPath = menu.ReactWebPath.trim();
  const separator = cleanPath.includes("?") ? "&" : "?";
  const queryParams = new URLSearchParams();

  queryParams.append("mdlId", menu.MdlId);
  if (menu.TxnType) queryParams.append("TxnType", menu.TxnType);
  if (menu.TxnSubType) queryParams.append("TxnSubType", menu.TxnSubType);

  return `${cleanPath}${separator}${queryParams.toString()}`;
};

const navigateToTicketingSystem = () => {
  const url = buildMenuUrl(ticketMenu);

  if (url) {
    window.location.href = url;
  } else {
    console.warn("Ticketing System menu not found");
  }
};

const navigateToCompanyUpdate = () => {
  const url = buildMenuUrl(companyUpdateMenu);

  if (url) {
    window.location.href = url;
  } else {
    console.warn("Company Update menu not found");
  }
};

const navigateToPropertySetup = () => {
  const url = buildMenuUrl(propertySetupMenu);

  if (url) {
    window.location.href = url;
  } else {
    console.warn("Property Setup menu not found");
  }
};


const loadApprovalCount = async () => {
  try {
    const userId = sessionStorage.getItem("userId");

    const res = await api.GetApprovalCount(userId);
   
    // ✅ FIXED PATH
    const count = res?.Table?.[0]?.PendingCount || 0;

    setApprovalCount(count);
  } catch (err) {
    console.error("Approval count error", err);
  }
};

const getMailName = (email) => {
  if (!email) return "Mail";
  return String(email).split("@")[0] || email;
};

const loadMailOutbox = useCallback(async () => {
  setMailLoading(true);
  setMailError("");

  try {
    const currentUserName = String(sessionStorage.getItem("userName") || "")
      .trim()
      .toLowerCase();

    const res = await api.GetMailOutbox();

    const rows = res?.data?.Table || res?.Data?.Table || res?.Table || [];

    const currentUserRows = rows.filter((row) => {
      const rowUserName = String(row.Username || row.UserName || row.userName || "")
        .trim()
        .toLowerCase();

      return rowUserName === currentUserName;
    });

    setMailItems(
      currentUserRows.map((row, index) => {
        const generatedAt =
          row.MailTime || row.GenerateTime || row.GeneratedTime || new Date().toISOString();

        const fileName = row.FileName || "";
        const fileUrl = row.FileUrl || "";

        return {
          id: row.logId ?? row.LogId ?? row.PrimeId ?? index,
          logId: row.logId ?? row.LogId ?? null,
          username: row.Username || row.UserName || row.userName || "",
          senderName: getMailName(row.FromEMailid || row.FromEmailId),
          from: row.FromEMailid || row.FromEmailId || "",
          to: row.ToEMailid || row.ToEmailId || "",
          subject: row.Subject || "(No subject)",
          preview: "",
          attachmentCount: fileName ? 1 : 0,
          status: Number(row.MailStatus ?? 0),
          generatedAt: new Date(generatedAt),
          body: row.MailContent || "",
          mdlId: row.Mdlid || row.MdlId || row.mdlId || "",
          primeId: row.PrimeId ?? row.Primeid ?? row.primeId ?? "",
          seqId:
            row.SeqId ??
            row.Seqid ??
            row.seqId ??
            String(row.MailContent || "").match(/mdldirect=[^!&"']*!([^!&"']+)!/i)?.[1] ??
            "",
          fileName,
          fileUrl
        };
      })
    );
  } catch (err) {
    console.error("Mail outbox load error", err);
    setMailItems([]);
    setMailError("Unable to load mail outbox");
  } finally {
    setMailLoading(false);
  }
}, []);

useEffect(() => {
  loadMailOutbox();
}, [loadMailOutbox]);

useEffect(() => {
  if (!mailSendMessage) return undefined;

  const timer = window.setTimeout(() => {
    setMailSendMessage(null);
  }, 3000);

  return () => window.clearTimeout(timer);
}, [mailSendMessage]);

useEffect(() => {
  if (!isMailDragging) return undefined;

  const handleMailDragMove = (event) => {
    const nextX = mailDragStartRef.current.offsetX + event.clientX - mailDragStartRef.current.x;
    const nextY = mailDragStartRef.current.offsetY + event.clientY - mailDragStartRef.current.y;
    setMailPopupOffset({ x: nextX, y: nextY });
  };

  const handleMailDragEnd = () => {
    setIsMailDragging(false);
  };

  window.addEventListener("mousemove", handleMailDragMove);
  window.addEventListener("mouseup", handleMailDragEnd);

  return () => {
    window.removeEventListener("mousemove", handleMailDragMove);
    window.removeEventListener("mouseup", handleMailDragEnd);
  };
}, [isMailDragging]);

useEffect(() => {
  if (!isMailOpen) return undefined;

  const handleMailEscape = (event) => {
    if (event.key === "Escape") {
      setIsMailOpen(false);
      setSelectedMail(null);
      setMailSendMessage(null);
    }
  };

  window.addEventListener("keydown", handleMailEscape);
  return () => window.removeEventListener("keydown", handleMailEscape);
}, [isMailOpen]);

useEffect(() => {
  if (!selectedMail) {
    mailBodyLoadedIdRef.current = null;
    mailSelectionRef.current = null;
    setIsMailContentEditing(false);
    setIsMailToEditing(false);
    return;
  }

  if (mailBodyEditorRef.current && mailBodyLoadedIdRef.current !== selectedMail.id) {
    mailBodyEditorRef.current.innerHTML = selectedMail.body || "";
    mailBodyLoadedIdRef.current = selectedMail.id;
    mailSelectionRef.current = null;
    setIsMailContentEditing(false);
    setIsMailToEditing(false);
  }
}, [selectedMail]);

const handleSendMail = async () => {
  if (!selectedMail?.logId) {
    setMailSendMessage({ type: "error", text: "Select a mail to send" });
    return;
  }

  setMailSending(true);
  setMailSendMessage(null);
  try {
    const result = await api.SendMailByLogId(selectedMail.logId);
    const message = result?.message || result?.Message || "Mail sent successfully";
    setMailSendMessage({ type: "success", text: message });
    await loadMailOutbox();
  } catch (err) {
    console.error("Send mail error", err);
    setMailSendMessage({ type: "error", text: err.message || "Unable to send mail" });
  } finally {
    setMailSending(false);
  }
};

const handleSendAllMail = async () => {
  const currentUserName = String(sessionStorage.getItem("userName") || "")
    .trim()
    .toLowerCase();

  const pendingMails = mailItems.filter((mail) =>
    mail.status === 0 &&
    mail.logId &&
    String(mail.username || "").trim().toLowerCase() === currentUserName
  );

  if (pendingMails.length === 0) {
    setMailSendMessage({
      type: "error",
      text: "No pending mails available for this user"
    });
    return;
  }

  mailSendAllStopRef.current = false;
  setMailSendingAll(true);
  setMailSendMessage({
    type: "success",
    text: `Sending 0 of ${pendingMails.length} mails...`
  });

  let sentCount = 0;
  let failedCount = 0;

  try {
    for (const mail of pendingMails) {
      if (mailSendAllStopRef.current) break;

      try {
        const result = await api.SendMailByLogId(mail.logId);
        const status = result?.Status ?? result?.status;

        if (status === 1) {
          sentCount += 1;
        } else {
          failedCount += 1;
        }
      } catch (err) {
        failedCount += 1;
        console.error(`Send all mail error for logId ${mail.logId}`, err);
      }

      if (!mailSendAllStopRef.current) {
        setMailSendMessage({
          type: failedCount > 0 ? "error" : "success",
          text: `Sending ${sentCount + failedCount} of ${pendingMails.length} mails...`
        });
      }
    }

    if (mailSendAllStopRef.current) {
      setMailSendMessage({
        type: "error",
        text: `Sending stopped. ${sentCount} mail(s) sent.`
      });
    } else if (failedCount > 0) {
      setMailSendMessage({
        type: "error",
        text: `${sentCount} mail(s) sent, ${failedCount} failed.`
      });
    } else {
      setMailSendMessage({
        type: "success",
        text: `${sentCount} mail(s) sent successfully.`
      });
    }

    await loadMailOutbox();
  } finally {
    setMailSendingAll(false);
    mailSendAllStopRef.current = false;
  }
};
const handlePassSendAll = () => {
  if (!mailSendingAll) return;
  mailSendAllStopRef.current = true;
  setMailSendMessage({ type: "error", text: "Stopping mail sending after the current mail..." });
};

const handleUpdateMailContent = async () => {
  if (!selectedMail?.logId) {
    setMailSendMessage({ type: "error", text: "Select a mail to update" });
    return;
  }

  setMailUpdating(true);
  setMailSendMessage(null);
  try {
    const result = await api.UpdateMailContent(selectedMail.logId, selectedMail.body || "", selectedMail.to || "");
    const message = result?.message || result?.Message || "Mail content updated successfully";
    setMailSendMessage({ type: "success", text: message });
    await loadMailOutbox();
  } catch (err) {
    console.error("Update mail content error", err);
    setMailSendMessage({ type: "error", text: err.message || "Unable to update mail content" });
  } finally {
    setMailUpdating(false);
  }
};

const handleDeleteMail = async (mail, event) => {
  event.stopPropagation();

  if (!mail?.logId) {
    setMailSendMessage({ type: "error", text: "Select a mail to delete" });
    return;
  }

  setMailDeletingLogId(mail.logId);
  setMailSendMessage(null);
  try {
    const result = await api.DeleteMailOutBox(mail.logId);
    const message = result?.message || result?.Message || "Mail deleted successfully.";
    setMailSendMessage({ type: "success", text: message });
    if (selectedMail?.id === mail.id) {
      setSelectedMail(null);
    }
    await loadMailOutbox();
  } catch (err) {
    console.error("Delete mail error", err);
    setMailSendMessage({ type: "error", text: err.message || "Unable to delete mail" });
  } finally {
    setMailDeletingLogId(null);
  }
};

const handleMailBodyEdit = (html) => {
  if (!selectedMail) return;

  setSelectedMail((prev) => prev ? { ...prev, body: html } : prev);
  setMailItems((prev) =>
    prev.map((mail) =>
      mail.id === selectedMail.id ? { ...mail, body: html } : mail
    )
  );
};

const handleMailToEdit = (toEMailid) => {
  if (!selectedMail) return;

  setSelectedMail((prev) => prev ? { ...prev, to: toEMailid } : prev);
  setMailItems((prev) =>
    prev.map((mail) =>
      mail.id === selectedMail.id ? { ...mail, to: toEMailid } : mail
    )
  );
};

const handleFetchVendorToAddress = async () => {
  if (!selectedMail?.logId) {
    setMailSendMessage({ type: "error", text: "Select a mail to fetch vendor address" });
    return;
  }

  setVendorToLoading(true);
  setMailSendMessage(null);
  try {
    const result = await api.GetVendorToAddress(selectedMail.logId);
    const toEMailid =
      result?.data?.Table?.[0]?.ToEMailid ||
      result?.Data?.Table?.[0]?.ToEMailid ||
      result?.Table?.[0]?.ToEMailid ||
      "";

    if (!toEMailid) {
      setMailSendMessage({ type: "error", text: "Vendor mail address not found" });
      return;
    }

    handleMailToEdit(toEMailid);
    setMailSendMessage({ type: "success", text: result?.message || result?.Message || "Vendor mail address fetched" });
  } catch (err) {
    console.error("Vendor mail address fetch error", err);
    setMailSendMessage({ type: "error", text: err.message || "Unable to fetch vendor mail address" });
  } finally {
    setVendorToLoading(false);
  }
};

const syncMailEditorBody = () => {
  if (!mailBodyEditorRef.current) return;
  handleMailBodyEdit(mailBodyEditorRef.current.innerHTML);
};

const saveMailEditorSelection = () => {
  const editor = mailBodyEditorRef.current;
  const selection = window.getSelection();
  if (!editor || !selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0);
  if (editor.contains(range.commonAncestorContainer)) {
    mailSelectionRef.current = range.cloneRange();
  }
};

const restoreMailEditorSelection = () => {
  const selection = window.getSelection();
  const range = mailSelectionRef.current;
  if (!selection || !range) return;

  selection.removeAllRanges();
  selection.addRange(range);
};

const handleMailFormatCommand = (command, value = null) => {
  if (!selectedMail || !mailBodyEditorRef.current) return;

  mailBodyEditorRef.current.focus();
  restoreMailEditorSelection();
  document.execCommand(command, false, value);
  saveMailEditorSelection();
  syncMailEditorBody();
};

const handleMailFormatBlock = (value) => {
  handleMailFormatCommand("formatBlock", value);
};

const handleMailCreateLink = async () => {
  const editor = mailBodyEditorRef.current;
  const range = mailSelectionRef.current;

  if (!selectedMail || !editor || !range || range.collapsed || !editor.contains(range.commonAncestorContainer)) {
    setMailSendMessage({ type: "error", text: "Select the mail content that should become a hyperlink." });
    return;
  }

  if (!selectedMail.mdlId || selectedMail.primeId === "" || selectedMail.primeId == null) {
    setMailSendMessage({ type: "error", text: "Module or document id was not found for this mail." });
    return;
  }

  setMailLinkInserting(true);
  setMailSendMessage(null);
  try {
    let moduleSeqId = selectedMail.seqId;

    if (!moduleSeqId) {
      const cachedMenus = JSON.parse(sessionStorage.getItem("allSubMenuList") || "{}");
      const menuRows = cachedMenus?.Table || cachedMenus?.table || [];
      const moduleMenu = menuRows.find(
        (menu) => String(menu.MdlId || menu.Mdlid || "").toLowerCase() === String(selectedMail.mdlId).toLowerCase()
      );
      moduleSeqId = moduleMenu?.SeqId || moduleMenu?.Seqid || moduleMenu?.seqId;
    }

    if (!moduleSeqId) {
      const moduleRes = await api.GetModuleByMdlId(selectedMail.mdlId);
      moduleSeqId =
        moduleRes?.seqId ||
        moduleRes?.SeqId ||
        moduleRes?.data?.seqId ||
        moduleRes?.data?.SeqId ||
        moduleRes?.Data?.seqId ||
        moduleRes?.Data?.SeqId;
    }

    if (!moduleSeqId) {
      throw new Error("Module sequence id was not found.");
    }

    const databaseKey = sessionStorage.getItem("databaseKey") || "Pgpl2026";
    const moduleDirect = `0!${encodeURIComponent(moduleSeqId)}!${encodeURIComponent(selectedMail.primeId)}`;
    const hyperlink = `${window.location.origin}/?dBKEY=${encodeURIComponent(databaseKey)}&mdldirect=${moduleDirect}`;

    editor.focus();
    restoreMailEditorSelection();
    document.execCommand("createLink", false, hyperlink);
    saveMailEditorSelection();
    syncMailEditorBody();
    setMailSendMessage({ type: "success", text: "Hyperlink inserted into the selected mail content." });
  } catch (err) {
    console.error("Mail hyperlink insert failed", err);
    setMailSendMessage({ type: "error", text: err.message || "Unable to insert hyperlink." });
  } finally {
    setMailLinkInserting(false);
  }
};

const handleMailClearFormat = () => {
  handleMailFormatCommand("removeFormat");
};

const toggleMailContentEditMode = () => {
  setIsMailContentEditing((prev) => {
    const next = !prev;
    if (!next) {
      syncMailEditorBody();
      mailSelectionRef.current = null;
    } else {
      window.setTimeout(() => {
        mailBodyEditorRef.current?.focus();
      }, 0);
    }
    return next;
  });
};

const toggleMailToEditMode = () => {
  setIsMailToEditing((prev) => !prev);
};

const handleMailPopupDragStart = (event) => {
  if (event.button !== 0) return;
  if (event.target.closest("button, input, select, textarea, label, a")) return;

  mailDragStartRef.current = {
    x: event.clientX,
    y: event.clientY,
    offsetX: mailPopupOffset.x,
    offsetY: mailPopupOffset.y
  };
  setIsMailDragging(true);
};

const resizeMailPopup = (direction) => {
  const sizes = ["compact", "normal", "large"];
  const currentIndex = sizes.indexOf(mailPopupSize);
  const nextIndex = direction === "in"
    ? Math.min(currentIndex + 1, sizes.length - 1)
    : Math.max(currentIndex - 1, 0);

  setMailPopupSize(sizes[nextIndex]);
};

const loadTasks = async () => {
  try {
    const userName = sessionStorage.getItem("userName");
    const res = await api.GetUserTaskList(userName);

    setTaskList(res?.Table || []);
    setTaskCount(res?.Table1?.[0]?.TaskCount || 0);
    const links = res?.taskList?.Table2 ?? res?.Table2 ?? [];
    setTaskLinks(links);
    setTaskStatusOptions(res?.taskList?.Table3 ?? res?.Table3 ?? []);
    setTaskLogs(res?.taskList?.Table4 ?? res?.Table4 ?? []);
  } catch (err) {
    console.error("Task load error", err);
  }
};




const getLinksByTask = (taskId) => {
  if (!taskId) return [];
  const normalizedId = String(taskId);
  return taskLinks.filter((link) => String(link.TaskId) === normalizedId);
};

const getLogsByTask = (taskId) => {
  if (!taskId) return [];
  const normalizedId = String(taskId);
  return taskLogs.filter((log) => String(log.TaskId) === normalizedId);
};

const formatTaskLogTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
};

const getTaskNoticeDateKey = (task) => getDateKey(task?.Due_date || task?.Start_date);
const noticeSelectedDateKey = getDateKey(noticeSelectedDate);
const noticeTodayKey = getDateKey(new Date());
const noticeMonthLabel = noticeMonth.toLocaleDateString("en-US", {
  month: "long",
  year: "numeric"
});
const noticeSelectedDateLabel = noticeSelectedDate.toLocaleDateString("en-US", {
  weekday: "short",
  month: "short",
  day: "numeric"
});

const tasksForNoticeDate = taskList.filter(
  (task) => getTaskNoticeDateKey(task) === noticeSelectedDateKey
);
const tasksForNoticeRange = taskList.filter((task) => {
  const taskDate = getTaskNoticeDateKey(task);
  return taskDate && taskDate >= appliedNoticeFromDate && taskDate <= appliedNoticeToDate;
});
const noticeTaskCountByDate = taskList.reduce((acc, task) => {
  const key = getTaskNoticeDateKey(task);
  if (!key) return acc;
  acc[key] = (acc[key] || 0) + 1;
  return acc;
}, {});

const filteredNoticeRangeTasks = tasksForNoticeRange.filter((task) => {
  const q = noticeSearch.trim().toLowerCase();
  if (!q) return true;

  return [
    task.Title,
    task.Description,
    task.UserName,
    task.Priority,
    task.Status
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
});

const applyNoticeDateRange = () => {
  if (!noticeFromDate || !noticeToDate) return;

  if (noticeFromDate <= noticeToDate) {
    setAppliedNoticeFromDate(noticeFromDate);
    setAppliedNoticeToDate(noticeToDate);
  } else {
    setNoticeFromDate(noticeToDate);
    setNoticeToDate(noticeFromDate);
    setAppliedNoticeFromDate(noticeToDate);
    setAppliedNoticeToDate(noticeFromDate);
  }
};

const filteredNoticeDateTasks = tasksForNoticeDate.filter((task) => {
  const q = noticeSearch.trim().toLowerCase();
  if (!q) return true;

  return [
    task.Title,
    task.Description,
    task.UserName,
    task.Priority,
    task.Status
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
});

const noticeCalendarDays = (() => {
  const year = noticeMonth.getFullYear();
  const month = noticeMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mondayStartOffset = (firstDay.getDay() + 6) % 7;
  const days = [];

  for (let index = 0; index < mondayStartOffset; index += 1) {
    days.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    days.push(new Date(year, month, day));
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
})();

const changeNoticeMonth = (offset) => {
  setNoticeMonth((value) => new Date(value.getFullYear(), value.getMonth() + offset, 1));
};

const goToNoticeToday = () => {
  const today = new Date();
  setNoticeSelectedDate(today);
  setNoticeMonth(today);
};

  const totalPages = Math.ceil(taskList.length / ITEMS_PER_PAGE);
  const displayTotalPages = totalPages > 0 ? totalPages : 1;
  const paginatedTaskList = taskList.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const paginationNeeded = totalPages > 1;

  useEffect(() => { 
    if (currentPage > displayTotalPages) {
      setCurrentPage(displayTotalPages);
    }
  }, [currentPage, displayTotalPages]);

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), displayTotalPages);
    if (nextPage === currentPage) return;
    setCurrentPage(nextPage);
  };
  useEffect(() => {
  const handleScroll = () => {
    setScrolled(window.scrollY > 10);
  };
  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
}, []);

    useEffect(() => {
	  const details = sessionStorage.getItem("companyDetails");
	  const name = sessionStorage.getItem("userName");
	
	  if (details) {
	    setCompanyDetails(JSON.parse(details));
	  } else {
	    navigate("/");
	  }

  if (name) setUserName(name);
}, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadQuickActionMenus = async () => {
      const userId = sessionStorage.getItem("userId") || localStorage.getItem("userId");
      if (!userId) {
        setTicketMenu(null);
        setCompanyUpdateMenu(null);
        setPropertySetupMenu(null);
        return;
      }

      try {
        const cachedMenus = sessionStorage.getItem("allSubMenuList");
        let allMenus = cachedMenus ? JSON.parse(cachedMenus) : null;

        if (!allMenus?.Table) {
          allMenus = await api.GetAllSubMenuList(userId);
          if (allMenus) {
            sessionStorage.setItem("allSubMenuList", JSON.stringify(allMenus));
          }
        }

        const menus = allMenus?.Table || [];
        const ticketMenuItem = menus.find(
          (item) => item?.MdlId === TICKET_MODULE_ID
        );
        const companyUpdateMenuItem = menus.find(
          (item) => item?.MdlId === COMPANY_UPDATE_MODULE_ID
        );
        const propertySetupMenuItem = menus.find(
          (item) => item?.MdlId === PROPERTY_SETUP_MODULE_ID
        );

        setTicketMenu(ticketMenuItem || null);
        setCompanyUpdateMenu(companyUpdateMenuItem || null);
        setPropertySetupMenu(propertySetupMenuItem || null);
      } catch (error) {
        console.error("Failed to load quick action menus:", error);
        setTicketMenu(null);
        setCompanyUpdateMenu(null);
        setPropertySetupMenu(null);
      }
    };

    loadQuickActionMenus();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsPopupOpen(false);
      }
    };
    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const perms = await getPermissions();
        setPermissions(perms);
      } catch (error) {
        console.error('Failed to load permissions:', error);
      }
    };
    loadPermissions();
  }, []);

const toggleTask = (taskId) => {
  setOpenTaskId(openTaskId === taskId ? null : taskId);
};

const selectedTask = taskList.find((task) => task.TaskId === openTaskId) || null;
const selectedTaskLogs = selectedTask ? getLogsByTask(selectedTask.TaskId) : [];

const resetTaskLogForm = useCallback((status = "") => {
  setShowTaskLogForm(false);
  setTaskLogNotes("");
  setTaskLogStatus(status);
  setTaskLogMessage(null);
  setTaskLogSaving(false);
  setTaskLogDeletingId(null);
}, []);

useEffect(() => {
  if (!selectedTask) {
    taskLogOpenTaskRef.current = null;
    return;
  }

  if (taskLogOpenTaskRef.current === selectedTask.TaskId) return;

  const fallbackStatus = selectedTask.Status || taskStatusOptions?.[0]?.Status || "";
  taskLogOpenTaskRef.current = selectedTask.TaskId;
  resetTaskLogForm(fallbackStatus);
}, [selectedTask, taskStatusOptions, resetTaskLogForm]);

const handleSaveTaskLog = async () => {
  if (!selectedTask || taskLogSaving) return;

  const notes = taskLogNotes.trim();
  if (!notes) {
    setTaskLogMessage({ type: "error", text: "Please enter notes before adding the log." });
    return;
  }

  try {
    setTaskLogSaving(true);
    setTaskLogMessage(null);

    const res = await api.SaveUpdateTaskLog({
      tlogId: 0,
      taskId: selectedTask.TaskId,
      notes,
      status: taskLogStatus || selectedTask.Status || "",
      updateBy: sessionStorage.getItem("userName") || userName
    });

    setTaskLogNotes("");
    setTaskLogMessage({
      type: "success",
      text: res?.message || "Task log saved successfully."
    });
    setShowTaskLogForm(false);

    await api.MarkTaskAsRead({
      taskId: selectedTask.TaskId,
      userName: sessionStorage.getItem("userName") || userName,
      isRead: 1
    });

    await loadTasks();
  } catch (err) {
    console.error("Task log save error", err);
    setTaskLogMessage({
      type: "error",
      text: err?.message || "Unable to save task log."
    });
  } finally {
    setTaskLogSaving(false);
  }
};

const handleDeleteTaskLog = async (log) => {
  if (!log?.TlogId || taskLogDeletingId) return;

  const confirmed = window.confirm("Delete this task log?");
  if (!confirmed) return;

  try {
    setTaskLogDeletingId(log.TlogId);
    setTaskLogMessage(null);

    const res = await api.DeleteTaskLog(log.TlogId);
    setTaskLogMessage({
      type: "success",
      text: res?.message || "Task log deleted successfully."
    });
    await loadTasks();
  } catch (err) {
    console.error("Task log delete error", err);
    setTaskLogMessage({
      type: "error",
      text: err?.message || "Unable to delete task log."
    });
  } finally {
    setTaskLogDeletingId(null);
  }
};

const handleMarkAsRead = async (taskId) => {
  const userName = sessionStorage.getItem("userName");

  try {
    // ✅ Step 1: Mark as read
    await api.MarkTaskAsRead({
      taskId: taskId,
      userName: userName,
      isRead: 1
    });

    // ✅ Step 2: Reload full data
    await loadTasks();

    // ✅ Step 3: Close popup
    setOpenTaskId(null);
    resetTaskLogForm();

  } catch (err) {
    console.error("Mark read error", err);
  }
};

const filteredMailItems = mailItems.filter((mail) => {
  const isOutboxMail = mail.status === 0;
  if (mailFolder === "outbox" && !isOutboxMail) return false;
  if (mailFolder === "sent" && isOutboxMail) return false;

  const query = mailSearch.trim().toLowerCase();
  if (!query) return true;
  return [mail.from, mail.to, mail.subject, mail.preview]
    .join(" ")
    .toLowerCase()
    .includes(query);
});
const selectedMailIndex = selectedMail
  ? filteredMailItems.findIndex((mail) => mail.id === selectedMail.id)
  : -1;
const canOpenPreviousMail = selectedMailIndex > 0;
const canOpenNextMail = selectedMailIndex >= 0 && selectedMailIndex < filteredMailItems.length - 1;

const handleMailNavigation = (direction) => {
  const nextIndex = selectedMailIndex + direction;
  const nextMail = filteredMailItems[nextIndex];
  if (!nextMail) return;

  setSelectedMail(nextMail);
  setMailSendMessage(null);
};
const mailFolderLabel = mailFolder === "outbox" ? "outbox" : "sent items";
const getOutboxCount = () => {
  return mailItems.filter((mail) => Number(mail.status) === 0).length;
};

useEffect(() => {
  if (!showLogoutMailConfirm) return;

  const handleEsc = (event) => {
    if (event.key === "Escape") {
      setShowLogoutMailConfirm(false);

      // Redirect to Main Page
      navigate("/explorer"); // change route if needed
    }
  };

  window.addEventListener("keydown", handleEsc);

  return () => {
    window.removeEventListener("keydown", handleEsc);
  };
}, [showLogoutMailConfirm, navigate]);

useEffect(() => {
  if (!isBackupModalOpen) return undefined;

  const handleBackupEscape = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setIsBackupModalOpen(false);
    }
  };

  window.addEventListener("keydown", handleBackupEscape, true);
  return () => window.removeEventListener("keydown", handleBackupEscape, true);
}, [isBackupModalOpen]);

const openDatabaseBackupModal = () => {
  if (backupLoading) return;

  setIsPopupOpen(false);
  setBackupResult(null);
  setIsBackupModalOpen(true);
};

const handleDatabaseBackup = async () => {
  if (backupLoading) return;

  setBackupLoading(true);
  setBackupResult(null);

  try {
    const result = await api.DatabaseBackup();
    const status = Number(result?.Status ?? result?.status ?? 0);

    if (status === 1) {
      setBackupResult({ type: "success", data: result });
    } else {
      setBackupResult({
        type: "error",
        message: result?.Message || result?.message || "Database backup failed.",
        error: result?.Error || result?.error || ""
      });
    }
  } catch (err) {
    setBackupResult({
      type: "error",
      message: "Database backup failed.",
      error: err?.message || String(err)
    });
  } finally {
    setBackupLoading(false);
  }
};
const handleLogout = () => {
  const outboxCount = mailItems.filter((mail) => Number(mail.status) === 0).length;

  if (outboxCount > 0) {
    setLogoutOutboxCount(outboxCount);
    setShowLogoutMailConfirm(true);
    setIsPopupOpen(false);
    return;
  }

  proceedLogout();
};


const proceedLogout = async () => {
  const logId = sessionStorage.getItem("LogId");
  const userId = sessionStorage.getItem("userId");
  const currentAuthToken = sessionStorage.getItem("CurrentAuthToken");

  try {
    if (!logId || !userId) {
      alert("Logout failed: session expired");
      return;
    }

    await api.LogoutUser(logId, userId, currentAuthToken);
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    AUTH_STORAGE_KEYS.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
    navigate("/", { replace: true });
  }
};

  const handleResetPassword = async () => {
    if (resetStep === 1) {
      // Step 1: Request reset password
      if (!emailId || !newPassword) {
        setResetMessage({ text: "Please enter email and new password", type: "error" });
        return;
      }
      setResetLoading(true);
      try {
        const userName = sessionStorage.getItem("userName");
        const result = await api.RequestResetPassword({ userName, emailId, newPassword });
        setResetMessage({ text: result.message || "OTP sent to your email", type: "success" });
        setResetStep(2);
      } catch (err) {
        setResetMessage({ text: "Request failed: " + err.message, type: "error" });
      }
      setResetLoading(false);
    } else {
      // Step 2: Confirm with OTP
      if (!otp) {
        setResetMessage({ text: "Please enter the OTP", type: "error" });
        return;
      }
      setResetLoading(true);
      try {
        const userName = sessionStorage.getItem("userName");
        const result = await api.ConfirmResetPassword({ resetOtp: otp, userName });
        const message = typeof result === 'string' ? result : (result.message || "Password updated successfully");
        if (message.includes("Invalid or expired OTP")) {
          setResetMessage({ text: message, type: "error" });
        } else {
          setResetMessage({ text: message, type: "success" });
          setTimeout(() => {
            setIsResetModalOpen(false);
            setEmailId("");
            setNewPassword("");
            setOtp("");
            setResetMessage(null);
            setResetStep(1);
          }, 2000);
        }
      } catch (err) {
        setResetMessage({ text: "Confirmation failed: " + err.message, type: "error" });
      }
      setResetLoading(false);
    }
  };

  const formatDateTime = (date) => {
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatMailDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    return date.toLocaleString('en-IN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };


return (
  <>
    {/* ================= HEADER ================= */}
    <div
      className={`app-header w-full fixed top-0 left-0 z-30 backdrop-blur-md shadow-md
      py-2 px-4 flex items-center justify-between rounded-lg transition-all duration-300
      ${scrolled ? "bg-base-100/40" : "bg-base-100/80"}`}
    >

    {/* LEFT SECTION — Toggle + Logo + Company Name */}
    <div className="app-header-left flex items-center gap-3">
      {onToggleSidebar && (
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg bg-base-300 hover:bg-base-400 text-base-content"
        >
          ☰
        </button>
      )}
      <img
        src={companyDetails?.logoUrl}
        alt="Company Logo"
        className="app-header-logo object-cover rounded-lg shadow-sm w-13 h-9 sm:w-17 sm:h-11"
      />


      {companyDetails?.companyDetails?.Table?.[0] && (
        <h1 className="app-header-title hidden text-lg font-bold tracking-wide sm:block md:text-xl text-base-content">
          {companyDetails.companyDetails.Table[0].ComName}
        </h1>
      )}
    </div>

    {/* CENTER SECTION — Page actions */}
    <div className="app-header-center items-center justify-end flex-1 hidden px-4 sm:flex">
      {headerActions}
    </div>
 <div className="app-header-right relative flex items-start gap-3 mr-3">

  {/* ✅ APPROVAL BUTTON */}
  {permissions.pendingApprovals === 1 && (
  <div className="header-action">
    <button
  onClick={navigateToApproval}
  className="top-icon-button"
  title="Pending Approvals"
  aria-label="Pending Approvals"
>
  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" />
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 14l2 2 4-4" />
  </svg>

  {approvalCount > 0 && (
    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full animate-bounce">
      {approvalCount}
    </span>
  )}
</button>
  <span className="hidden top-icon-label sm:block">
    Pending Approvals
  </span>
  </div>
  )}


  {/* 🔔 NOTIFICATION BUTTON */}
  {permissions.noticeboard === 1 && (
  <div className="header-action">
    <button
      onClick={() => setShowNotice(!showNotice)}
      className="top-icon-button"
      aria-label="Toggle noticeboard"
      title="Noticeboard"
    >
      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a3 3 0 006 0" />
      </svg>

      {taskCount > 0 && (
        <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full animate-bounce">
          {taskCount}
        </span>
      )}
    </button>
    <span className="hidden top-icon-label sm:block">
      Noticeboard
    </span>

    {showNotice && (
    <div
  className="absolute right-0 z-50 mt-2 mx-2 flex w-[900px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:mx-0"
  style={{ height: "calc(100vh - 80px)", maxHeight: "calc(100vh - 80px)" }}
>
       <div className="flex items-center gap-4 px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
  <div className="min-w-[170px]">
    <h3 className="font-semibold">Noticeboard</h3>
    <div className="text-xs text-blue-100">Tasks by due date</div>
  </div>
  

  <div className="relative flex-1 max-w-[420px]">
    <input
      type="search"
      value={noticeSearch}
      onChange={(e) => setNoticeSearch(e.target.value)}
      placeholder="Search tasks..."
      className="w-full pr-3 text-sm font-medium border outline-none h-9 rounded-xl border-white/30 bg-white/95 pl-9 text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-white/40"
    />
    <svg
      className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
    </svg>
  </div>

  <button
    onClick={() => setShowNotice(false)}
    className="flex items-center justify-center w-8 h-8 ml-auto text-white transition rounded-full hover:bg-white/15"
    aria-label="Close noticeboard"
  >
    x
  </button>
</div>

        <div className="grid min-h-0 flex-1 grid-cols-[450px_450px] overflow-hidden bg-slate-50">
          <div className="flex flex-col min-h-0 bg-white border-r border-slate-200">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
             
              <div>
                <div className="text-sm font-semibold text-slate-800">
                  {noticeSelectedDateKey === noticeTodayKey ? "Today" : noticeSelectedDateLabel}
                </div>
                <div className="text-xs text-slate-500">
                  {filteredNoticeDateTasks.length} {filteredNoticeDateTasks.length === 1 ? "task" : "tasks"}
                </div>
              </div>
              <button
                type="button"
                onClick={goToNoticeToday}
                className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
              >
                Today
              </button>
            </div>

            <div className="h-[180px] shrink-0 space-y-2 overflow-y-auto p-3">
              {filteredNoticeDateTasks.map((task) => (
                <div
                  key={task.TaskId}
                  className={`cursor-pointer rounded-xl border p-3 shadow-sm transition ${
                    task.IsRead
                      ? "border-emerald-100 bg-emerald-50/70 hover:bg-emerald-50"
                      : "border-red-100 bg-red-50/70 hover:bg-red-50"
                  }`}
                  onClick={() => toggleTask(task.TaskId)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`truncate text-sm font-semibold ${
                        task.IsRead ? "text-emerald-800" : "text-red-800"
                      }`}>
                        {task.Title}
                      </div>
                      <div className="mt-1 text-xs line-clamp-2 text-slate-600">
                        {task.Description}
                      </div>
                    </div>
                    {!task.IsRead && (
                      <span className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
                        New
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-500">
                    <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-700">
                      {task.Priority || "-"}
                    </span>
                    <span>{task.Status || "-"}</span>
                    <span className="ml-auto font-medium">By - {task.UserName || "-"}</span>
                  </div>
                </div>
              ))}

             {filteredNoticeDateTasks.length === 0 && (
                <div className="flex items-center justify-center h-full min-h-0 p-4 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
                  No tasks for this date
                </div>
              )}
            </div>

            <div className="flex flex-col flex-1 min-h-0 p-2 border-t border-slate-200 bg-slate-50">
              <div className="mb-2 grid grid-cols-[1fr_1fr_auto] items-end gap-2 rounded-xl border border-slate-200 bg-white p-2">
                <label className="min-w-0">
                  <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">From Date</span>
                  <input
                    type="date"
                    value={noticeFromDate}
                    onChange={(event) => setNoticeFromDate(event.target.value)}
                    className="w-full h-8 px-2 text-xs transition bg-white border rounded-lg outline-none border-slate-300 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label className="min-w-0">
                  <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">To Date</span>
                  <input
                    type="date"
                    value={noticeToDate}
                    onChange={(event) => setNoticeToDate(event.target.value)}
                    className="w-full h-8 px-2 text-xs transition bg-white border rounded-lg outline-none border-slate-300 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <button
                  type="button"
                  onClick={applyNoticeDateRange}
                  className="flex items-center justify-center h-8 px-3 text-xs font-semibold text-white transition bg-blue-600 rounded-lg hover:bg-blue-700"
                  title="Refresh tasks"
                  aria-label="Refresh tasks"
                >
                  Refresh
                </button>
              </div>

              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Tasks By Date Range</div>
                  <div className="text-xs text-slate-500">
                    {filteredNoticeRangeTasks.length} {filteredNoticeRangeTasks.length === 1 ? "task" : "tasks"}
                  </div>
                </div>
                <div className="text-[10px] font-semibold text-slate-600">
                  {appliedNoticeFromDate} to {appliedNoticeToDate}
                </div>
              </div>

             <div className="flex-1 min-h-0 pr-2 overflow-y-auto scrollbar-thin">
                <div className="space-y-1">
                  {filteredNoticeRangeTasks.map((task) => (
                    <button
                      key={task.TaskId}
                      type="button"
                      onClick={() => toggleTask(task.TaskId)}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-sm shadow-sm transition ${
                        task.IsRead
                          ? "border-emerald-100 bg-emerald-50/70 hover:bg-emerald-50"
                          : "border-red-100 bg-red-50/70 hover:bg-red-50"
                      }`}
                      title={`By : ${task.UserName || "-"}`}
>
                      <div className="grid grid-cols-[38px_1fr_auto] items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-[11px] font-semibold text-slate-700">
                          {new Date(task.Due_date || task.Start_date).getDate()}
                        </div>
                        <div>
                          <div className="font-semibold truncate text-slate-800">{task.Title}</div>
                          <div className="mt-0.5 text-[11px] leading-tight text-slate-500 line-clamp-1">{task.Description}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 text-[10px] text-slate-500">
                         
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">{task.Priority || "-"}</span>
                          <span>{task.Status || "-"}</span>
                        </div>
                      </div>
                    </button>
                  ))}

                  {filteredNoticeRangeTasks.length === 0 && (
                    <div className="p-4 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
                      No tasks found between selected dates
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-slate-50">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => changeNoticeMonth(-1)}
                className="flex items-center justify-center w-8 h-8 transition rounded-full text-slate-600 hover:bg-white hover:text-blue-700"
                aria-label="Previous month"
              >
                &lt;
              </button>
              <div className="text-sm font-bold text-slate-800">{noticeMonthLabel}</div>
              <button
                type="button"
                onClick={() => changeNoticeMonth(1)}
                className="flex items-center justify-center w-8 h-8 transition rounded-full text-slate-600 hover:bg-white hover:text-blue-700"
                aria-label="Next month"
              >
                &gt;
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div key={day} className="py-1">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {noticeCalendarDays.map((date, index) => {
                const key = date ? getDateKey(date) : `empty-${index}`;
                const count = date ? noticeTaskCountByDate[getDateKey(date)] || 0 : 0;
                const isSelected = date && getDateKey(date) === noticeSelectedDateKey;
                const isToday = date && getDateKey(date) === noticeTodayKey;

                return date ? (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNoticeSelectedDate(date)}
                    aria-label={`${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${count ? `, ${count} ${count === 1 ? "task" : "tasks"}` : ", no tasks"}`}
                    className={`relative flex h-10 flex-col items-center justify-center rounded-xl border text-xs font-semibold shadow-sm transition ${
                      isSelected
                        ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
                        : isToday
                          ? "border-blue-200 bg-blue-100 text-blue-700 hover:bg-blue-200"
                          : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                    }`}
                  >
                    <span>{date.getDate()}</span>
                    {count > 0 && (
                      <span className={`mt-1 inline-flex min-w-[1.6rem] items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        isSelected
                          ? "bg-white text-blue-700"
                          : "bg-emerald-500 text-white"
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                ) : (
                  <div key={key} className="h-10" />
                );
              })}
            </div>

            <div className="p-3 mt-4 bg-white border rounded-xl border-slate-200">
              <div className="text-xs font-semibold text-slate-700">Month Tasks</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{taskList.length}</div>
              <div className="text-xs text-slate-400">Total visible noticeboard tasks</div>
            </div>
          </div>
        </div>
      </div>
    )}

    {false && showNotice && (
      <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border shadow-2xl w-96 rounded-2xl border-base-200">

            <div className="flex items-center justify-between px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
              <h3 className="font-semibold">Noticeboard</h3>
              <button onClick={() => setShowNotice(false)}>✕</button>
            </div>

            <div className="p-3 space-y-3 overflow-y-auto max-h-80">
              {paginatedTaskList.map((task) => (
                <div
                  key={task.TaskId}
              className={`p-3 border rounded-lg cursor-pointer transition duration-200 ${
                task.IsRead
                  ? 'bg-green-50 border-green-300 hover:bg-green-100'
                  : 'bg-red-50 border-red-300 hover:bg-red-100'
              }`}
              onClick={() => toggleTask(task.TaskId)}
            >
              <div className="flex items-center justify-between text-sm font-semibold">
               <div>
  <div className={`font-semibold ${
    task.IsRead ? 'text-green-700' : 'text-red-700'
  }`}>
    {task.Title}
  </div>

  <div className="flex items-center justify-between w-full gap-12 mt-1 text-xs text-gray-500">
  
  {/* LEFT */}
  <span className="truncate max-w-[60%]">
    By - {task.UserName}
  </span>

  {/* RIGHT */}
  <span className="ml-auto text-right whitespace-nowrap">
    {new Date(task.Due_date).toLocaleDateString("en-GB")}
  </span>

</div>
</div>

                <div className="flex items-center gap-2">
                  {!task.IsRead && (
                    <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded">New</span>
                  )}

                  <span>{openTaskId === task.TaskId ? '▲' : '▼'}</span>
                </div>
              </div>

            </div>
          ))}
        </div>
        {paginationNeeded && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-xs font-semibold transition rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
            >
              &lt;
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    onClick={() => goToPage(page)}
                    className={`px-2 py-1 text-xs font-semibold rounded-md transition ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-xs font-semibold transition rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
            >
              &gt;
            </button>
          </div>
        )}
      </div>
    )}
  </div>
  )}

  {/* MAIL BUTTON */}
  <div className="header-action">
    <button
      onClick={() => {
        setMailFolder("outbox");
        setSelectedMail(null);
        setMailSendMessage(null);
        setIsMailOpen(true);
      }}
      className="top-icon-button"
      aria-label="Open mail"
      title="Mail"
    >
      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16v12H4z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 6 8-6" />
      </svg>
      <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full">
        {mailItems.filter(x => x.status === 0).length}
      </span>
    </button>
    <span className="hidden top-icon-label sm:block">
      Mail
    </span>
  </div>

	<div className="app-user-area relative flex items-center self-center gap-2 pl-3 ml-1 border-l border-base-content/10">
<button
    onClick={() => setIsPopupOpen(!isPopupOpen)}
	    className="app-user-button flex items-center justify-center w-8 h-8 transition-colors rounded-full shadow-sm bg-base-100 text-base-content hover:bg-base-200"
    title="User Menu"
  >
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
        clipRule="evenodd"
      />
    </svg>
  </button>
  
  <span
    className="hidden sm:block text-sm font-medium text-base-content truncate max-w-[110px]"
    title={userName}
  >
    {userName}
  </span>

  {isPopupOpen && (
    <div
      ref={popupRef}
      className="absolute right-0 z-[1000] p-2 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl top-full dark:bg-gray-800 dark:border-gray-600 min-w-80"
    >
      <div className="flex items-center gap-2 px-3 py-2 mb-2 text-sm text-gray-900 border-b border-gray-300 dark:text-gray-100 dark:border-gray-600">
        <svg className="w-4 h-4 text-gray-900 dark:text-gray-100" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        Login as {userName}
      </div>
      {permissions.resetPassword === 1 && (
        <button
          onClick={() => {
            setIsResetModalOpen(true);
            setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-blue-600 transition-colors rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:text-blue-400"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
	          Change Password
	        </button>
	      )}
	      {ticketMenu && (
	        <button
	          onClick={() => {
	            navigateToTicketingSystem();
	            setIsPopupOpen(false);
	          }}
	          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
	        >
	          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
	            <path
	              strokeLinecap="round"
	              strokeLinejoin="round"
	              strokeWidth={2}
	              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 4v-4z"
	            />
	          </svg>
	         Report an Issue
	        </button>
	      )}
	      {/* {companyUpdateMenu && (
	        <button
	          onClick={() => {
	            navigateToCompanyUpdate();
	            setIsPopupOpen(false);
	          }}
	          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
	        >
	          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
	            <path
	              strokeLinecap="round"
	              strokeLinejoin="round"
	              strokeWidth={2}
	              d="M19 21H5a2 2 0 01-2-2V7a2 2 0 012-2h4l2-2h2l2 2h4a2 2 0 012 2v12a2 2 0 01-2 2zM9 13h6m-3-3v6"
	            />
	          </svg>
	          {companyUpdateMenu.MdlDisplayName || "Company Update"}
	        </button>
	      )} */}
	      {propertySetupMenu && (
	        <button
	          onClick={() => {
	            navigateToPropertySetup();
	            setIsPopupOpen(false);
	          }}
	          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
	        >
	          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
	            <path
	              strokeLinecap="round"
	              strokeLinejoin="round"
	              strokeWidth={2}
	              d="M3 21h18M5 21V7l8-4 6 3v15M9 9h1m-1 4h1m4-4h1m-1 4h1m-6 8v-4h6v4"
	            />
	          </svg>
		          {propertySetupMenu.MdlDisplayName || "Property Setup"}
		        </button>
		      )}
		      {/* <button
		        onClick={openDatabaseBackupModal}
		        disabled={backupLoading}
		        className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
		      >
		        {backupLoading ? (
		          <span className="w-4 h-4 border-2 rounded-full animate-spin border-slate-300 border-t-slate-700" />
		        ) : (
		          <Database className="w-4 h-4" />
		        )}
		        {backupLoading ? "Taking backup..." : "Database Backup"}
		      </button> */}
		      {permissions.todoList === 1 && (
	        <button
	          onClick={() => {
	            /* Todo list */ setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          Todo List
        </button>
      )}
      {permissions.activityLog === 1 && (
        <button
          onClick={() => {
            /* Activity Log */ setIsPopupOpen(false);
          }}
          className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          Activity Log
        </button>
      )}
      <button
        onClick={() => {
          handleLogout();
          setIsPopupOpen(false);
        }}
        className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
        Logout
      </button>
    </div>
  )}
</div>
</div>

    {/* RIGHT SECTION — Time + Logout */}
    <div className="app-header-time flex items-center flex-shrink-0 gap-4">
      {/* Time */}
      <span className="text-xs font-medium whitespace-nowrap md:text-sm text-base-content/70">
        {formatDateTime(currentTime)}
      </span>



    </div>

	    {isBackupModalOpen && (
	      <div
	        className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
	        onClick={() => !backupLoading && setIsBackupModalOpen(false)}
	      >
	        <div
	          onClick={(e) => e.stopPropagation()}
	          className="w-[460px] max-w-[90vw] overflow-hidden bg-white border border-blue-100 shadow-2xl rounded-2xl"
	        >
	          <div className="flex items-center justify-between px-5 py-4 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
	            <div>
	              <h3 className="text-lg font-bold">Database Backup</h3>
	              <p className="text-sm text-blue-100">
	                {backupResult ? "Backup result" : "Confirmation"}
	              </p>
	            </div>
	            <button
	              type="button"
	              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/20"
	              title="Close (Esc)"
	              onClick={() => setIsBackupModalOpen(false)}
	            >
	              ✕
	            </button>
	          </div>

	          <div className="p-5">
	            {!backupResult ? (
	              <>
	                <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-blue-50">
	                  <div className="flex items-center justify-center text-white bg-blue-600 rounded-full h-11 w-11">
	                    {backupLoading ? (
	                      <span className="w-5 h-5 border-2 rounded-full animate-spin border-blue-100 border-t-white" />
	                    ) : (
	                      <Database className="w-5 h-5" />
	                    )}
	                  </div>
	                  <div>
	                    <div className="font-bold text-slate-800">
	                      {backupLoading ? "Taking backup..." : "Do you want to take a database backup?"}
	                    </div>
	                    {backupLoading && (
	                      <div className="text-sm text-slate-500">
	                        Please wait while the backup is running.
	                      </div>
	                    )}
	                  </div>
	                </div>

	                <div className="flex justify-end gap-3">
	                  <button
	                    type="button"
	                    disabled={backupLoading}
	                    className="px-5 py-2 text-sm font-bold border rounded-full border-slate-300 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
	                    onClick={() => setIsBackupModalOpen(false)}
	                  >
	                    Cancel
	                  </button>
	                  <button
	                    type="button"
	                    disabled={backupLoading}
	                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
	                    onClick={handleDatabaseBackup}
	                  >
	                    {backupLoading && (
	                      <span className="w-4 h-4 border-2 rounded-full animate-spin border-blue-100 border-t-white" />
	                    )}
	                    {backupLoading ? "Taking backup..." : "Backup"}
	                  </button>
	                </div>
	              </>
	            ) : backupResult.type === "success" ? (
	              <>
	                {(() => {
	                  const data = backupResult.data || {};
	                  const storageMethod = data.StorageMethod || data.storageMethod || "";
	                  const googleDriveUrl = data.GoogleDriveUrl || data.googleDriveUrl || "";
	                  const localBackupPath = data.LocalBackupPath || data.localBackupPath || "";
	                  return (
	                    <>
	                      <div className="p-4 mb-4 border border-emerald-200 rounded-xl bg-emerald-50">
	                        <h4 className="mb-3 font-bold text-emerald-800">
	                          Database Backup Completed Successfully
	                        </h4>
	                        <div className="space-y-1 text-sm text-slate-700">
	                          <div><span className="font-semibold">Database:</span> {data.DatabaseKey || data.databaseKey || "-"}</div>
	                          <div><span className="font-semibold">File:</span> {data.BackupFileName || data.backupFileName || "-"}</div>
	                          <div><span className="font-semibold">Storage:</span> {storageMethod || "-"}</div>
	                          {String(storageMethod).toUpperCase() === "LOCAL" && localBackupPath && (
	                            <div className="break-words"><span className="font-semibold">Path:</span> {localBackupPath}</div>
	                          )}
	                        </div>
	                      </div>
	                      <div className="flex justify-end gap-3">
	                        <button
	                          type="button"
	                          className="px-5 py-2 text-sm font-bold border rounded-full border-slate-300 text-slate-600 hover:bg-slate-100"
	                          onClick={() => setIsBackupModalOpen(false)}
	                        >
	                          Close
	                        </button>
	                        {googleDriveUrl && (
	                          <a
	                            href={googleDriveUrl}
	                            target="_blank"
	                            rel="noopener noreferrer"
	                            className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700"
	                          >
	                            Open Google Drive
	                          </a>
	                        )}
	                      </div>
	                    </>
	                  );
	                })()}
	              </>
	            ) : (
	              <>
	                <div className="p-4 mb-4 border border-red-200 rounded-xl bg-red-50">
	                  <h4 className="mb-2 font-bold text-red-700">
	                    {backupResult.message || "Database backup failed."}
	                  </h4>
	                  {backupResult.error && (
	                    <div className="text-sm text-red-700 break-words">
	                      {backupResult.error}
	                    </div>
	                  )}
	                </div>
	                <div className="flex justify-end">
	                  <button
	                    type="button"
	                    className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700"
	                    onClick={() => setIsBackupModalOpen(false)}
	                  >
	                    Close
	                  </button>
	                </div>
	              </>
	            )}
	          </div>
	        </div>
	      </div>
	    )}

	    {/* Reset Password Modal */}
	    {isResetModalOpen && (
	      <>
	        <div className="fixed bottom-0 left-0 right-0 z-40 top-24 bg-black/40 backdrop-blur-md" onClick={() => setIsResetModalOpen(false)}></div>
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-start justify-center p-4 top-24" onClick={() => setIsResetModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 rounded-xl p-6 
                w-full max-w-xl md:max-w-xl lg:max-w-xl
                max-h-[70vh] overflow-y-auto shadow-xl">
               <h3 className="mb-4 text-xl font-semibold text-base-content">Change Password</h3>
               {resetStep === 1 ? (
                 <>
                 <div className="mb-4">
  <label className="block mb-1 text-sm font-medium text-base-content">
    Username
  </label>
  <input
    type="text"
    value={userName}
    disabled
    className="w-full px-3 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
  />
</div>
                   <div className="mb-4">
                     <label className="block mb-1 text-sm font-medium text-base-content">Email ID</label>
                     <input
                       type="email"
                       value={emailId}
                       onChange={(e) => setEmailId(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                       placeholder="Enter your email"
                     />
                   </div>
                   <div className="mb-4">
                     <label className="block mb-1 text-sm font-medium text-base-content">New Password</label>
                     <input
                       type="password"
                       value={newPassword}
                       onChange={(e) => setNewPassword(e.target.value)}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                       placeholder="Enter new password"
                     />
                   </div>
                 </>
               ) : (
                 <div className="mb-4">
                   <label className="block mb-1 text-sm font-medium text-base-content">Enter OTP</label>
                   <input
                     type="text"
                     value={otp}
                     onChange={(e) => setOtp(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
                     placeholder="Enter the OTP sent to your email"
                   />
                 </div>
               )}
            {resetMessage && (
              <div className={`mb-4 p-2 rounded-md text-sm ${resetMessage.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                {resetMessage.text}
              </div>
            )}
            <div className="flex justify-end gap-2">          
              <button
                onClick={handleResetPassword}
                disabled={resetLoading}
                className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-green-500 border border-green-600 rounded-md hover:bg-green-600 hover:border-green-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
              >
                {resetLoading ? (resetStep === 1 ? "Requesting..." : "Confirming...") : (resetStep === 1 ? "Request Change" : "Confirm Change")}
              </button>
                 <button
                onClick={() => {
                  setIsResetModalOpen(false);
                  setResetStep(1);
                  setEmailId("");
                  setNewPassword("");
                  setOtp("");
                  setResetMessage(null);
                }}
                className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-red-500 border border-red-600 rounded-md hover:bg-red-600 hover:border-red-700 hover:shadow-md hover:scale-105"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </>
    )}
  </div>

  {isMailOpen && (
    <div
      className="mail-overlay"
      onClick={() => {
        setIsMailOpen(false);
        setSelectedMail(null);
      }}
    >
      <div
        className={`mail-popup mail-popup-wide mail-size-${mailPopupSize} ${isMailListCollapsed ? "mail-list-collapsed" : ""} ${isMailDragging ? "mail-popup-dragging" : ""}`}
        style={{ transform: `translate(${mailPopupOffset.x}px, ${mailPopupOffset.y}px)` }}
        onClick={(e) => e.stopPropagation()}
      >
      <div className="mail-popup-header" onMouseDown={handleMailPopupDragStart}>
  <div className="mail-popup-title">
    <button
      type="button"
      className={`mail-list-toggle-btn ${isMailListCollapsed ? "mail-list-toggle-active" : ""}`}
      onClick={() => setIsMailListCollapsed((prev) => !prev)}
      title={isMailListCollapsed ? "Show mail list" : "Hide mail list"}
      aria-label={isMailListCollapsed ? "Show mail list" : "Hide mail list"}
    >
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        {isMailListCollapsed ? (
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
        )}
      </svg>
    </button>

    <div>
      <h2>Mail</h2>
      <span>{filteredMailItems.length} {mailFolderLabel} messages</span>
    </div>

    <div className="mail-title-actions">
      <button
        type="button"
        className="mail-send-btn mail-send-all-btn"
        onClick={handleSendAllMail}
        disabled={mailSendingAll}
      >
        Send All
      </button>

      <button
        type="button"
        className="mail-send-btn mail-pass-btn"
        onClick={handlePassSendAll}
        disabled={!mailSendingAll}
      >
        Pause
      </button>
    </div>
  </div>

  <div className="mail-popup-tools">

      <button
    type="button"
    className="mail-icon-btn mail-refresh-btn"
    onClick={loadMailOutbox}
    disabled={mailLoading}
    title="Refresh mails"
    aria-label="Refresh mails"
  >
    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0014-3M19 5a9 9 0 00-14 3"
      />
    </svg>
  </button>
    <div className="mail-search">
      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
      </svg>

      <input
        type="text"
        placeholder="Search mail"
        value={mailSearch}
        onChange={(e) => setMailSearch(e.target.value)}
      />
    </div>

    <button
      type="button"
      className="mail-icon-btn mail-zoom-btn"
      onClick={() => resizeMailPopup("out")}
      disabled={mailPopupSize === "compact"}
      title="Zoom out"
    >
      -
    </button>

    <button
      type="button"
      className="mail-icon-btn mail-zoom-btn"
      onClick={() => resizeMailPopup("in")}
      disabled={mailPopupSize === "large"}
      title="Zoom in"
    >
      +
    </button>

    <button
      type="button"
      className="mail-send-btn"
      onClick={handleSendMail}
      disabled={mailSending || mailSendingAll}
    >
      {mailSending ? "Sending..." : "Send"}
    </button>

    <button
      type="button"
      className="mail-icon-btn"
      onClick={() => {
        setIsMailOpen(false);
        setSelectedMail(null);
        setMailSendMessage(null);
      }}
      title="Close"
    >
      ×
    </button>
  </div>
</div>

        <div className="mail-workspace">
          <div className="mail-list-pane">
            <div className="mail-tabs" role="tablist" aria-label="Mail folders">
              <button
                type="button"
                className={mailFolder === "outbox" ? "active" : ""}
                onClick={() => {
                  setMailFolder("outbox");
                  setSelectedMail(null);
                  setMailSendMessage(null);
                }}
                role="tab"
                aria-selected={mailFolder === "outbox"}
              >
                Outbox
              </button>
                <button
                type="button"
                className={mailFolder === "sent" ? "active" : ""}
                onClick={() => {
                  setMailFolder("sent");
                  setSelectedMail(null);
                  setMailSendMessage(null);
                }}
                role="tab"
                aria-selected={mailFolder === "sent"}
              >
                Sent Items
              </button>
            </div>
            <div className="mail-list">
              {mailLoading && (
                <div className="mail-empty">Loading mail...</div>
              )}
              {!mailLoading && mailError && (
                <div className="mail-empty">{mailError}</div>
              )}
              {!mailLoading && !mailError && filteredMailItems.map((mail) => (
                <div
                  key={mail.id}
                  className={`mail-row ${selectedMail?.id === mail.id ? "mail-row-active" : ""} ${mail.status === 0 ? "mail-row-status-green" : "mail-row-status-red"}`}
                  onClick={() => {
                    setSelectedMail(mail);
                    setMailSendMessage(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedMail(mail);
                      setMailSendMessage(null);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <span className="mail-sender">
                    <strong>{mail.senderName}</strong>
                    <small>To: {mail.to}</small>
                  </span>
                  <span className="mail-subject">{mail.subject}</span>
                  <span className="mail-attachment">
                    {mail.attachmentCount > 0 && (
                      <span title={`${mail.attachmentCount} attachment${mail.attachmentCount > 1 ? "s" : ""}`}>
                        {mail.attachmentCount}
                      </span>
                    )}
                  </span>
                  <span className="mail-time"><span className="mail-time-label">Edit Time</span> {formatMailDateTime(mail.generatedAt)}</span>
                  <button
                    type="button"
                    className="mail-delete-btn"
                    onClick={(event) => handleDeleteMail(mail, event)}
                    disabled={mailDeletingLogId === mail.logId}
                    title="Delete mail"
                    aria-label="Delete mail"
                  >
                    {mailDeletingLogId === mail.logId ? (
                      <span className="mail-delete-spinner" aria-hidden="true"></span>
                    ) : (
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 7l1 13h10l1-13" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 7V4h6v3" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
              {!mailLoading && !mailError && filteredMailItems.length === 0 && (
                <div className="mail-empty">No mail found</div>
              )}
            </div>
          </div>

          <div className="mail-detail-pane">
            {mailSendMessage && (
              <div className={`mail-send-message mail-detail-message ${mailSendMessage.type === "error" ? "error" : "success"}`}>
                {mailSendMessage.text}
              </div>
            )}
            {selectedMail ? (
              <>
                <div className="mail-read-header">
                  <h2>{selectedMail.subject}</h2>
                  <div className="mail-read-navigation" aria-label="Navigate messages">
                    <button
                      type="button"
                      onClick={() => handleMailNavigation(-1)}
                      disabled={!canOpenPreviousMail}
                      title="Previous mail"
                      aria-label="Previous mail"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMailNavigation(1)}
                      disabled={!canOpenNextMail}
                      title="Next mail"
                      aria-label="Next mail"
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                  <span className="mail-read-generated"><strong>Generated:</strong> {formatDateTime(selectedMail.generatedAt)}</span>
                </div>
                <div className="mail-read-meta">
                  <div className="mail-read-to-row">
                    <strong>To:</strong>
                    {isMailToEditing ? (
                      <span className="mail-to-edit-wrap">
                        <input
                          type="email"
                          className="mail-to-input"
                          value={selectedMail.to}
                          onChange={(e) => handleMailToEdit(e.target.value)}
                          placeholder="To email address"
                        />
                        <button
                          type="button"
                          className="mail-vendor-address-btn"
                          onClick={handleFetchVendorToAddress}
                          disabled={vendorToLoading}
                          title="Fetch vendor mail address"
                          aria-label="Fetch vendor mail address"
                        >
                          {vendorToLoading ? (
                            <span className="mail-vendor-spinner" aria-hidden="true"></span>
                          ) : (
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 6 8-6" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 19h14a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M17 3v4h4" />
                            </svg>
                          )}
                        </button>
                      </span>
                    ) : (
                      <span>{selectedMail.to}</span>
                    )}
                    <button
                      className={`mail-edit-icon-btn ${isMailToEditing ? "mail-edit-icon-btn-active" : ""}`}
                      type="button"
                      onClick={toggleMailToEditMode}
                      title={isMailToEditing ? "Finish To address editing" : "Edit To address"}
                      aria-label={isMailToEditing ? "Finish To address editing" : "Edit To address"}
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.86 4.49l2.65 2.65M5 19l4.2-.7L19.2 8.29a1.88 1.88 0 000-2.66l-.83-.83a1.88 1.88 0 00-2.66 0L5.7 14.8 5 19z" />
                      </svg>
                    </button>
                  </div>
                  <div className="mail-read-meta-right">
                    <div><strong>From:</strong> {selectedMail.from}</div>
                  </div>
                </div>
                <div className={`mail-read-scroll ${isMailListCollapsed && selectedMail.fileUrl ? "mail-read-split" : ""}`}>
                  <div className="mail-content-column">
                  <div className="mail-body-actionbar">
                    <button
                      className={`mail-edit-toggle ${isMailContentEditing ? "mail-edit-toggle-active" : ""}`}
                      type="button"
                      onClick={toggleMailContentEditMode}
                      title={isMailContentEditing ? "Finish content editing" : "Edit mail content"}
                      aria-label={isMailContentEditing ? "Finish content editing" : "Edit mail content"}
                    >
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.86 4.49l2.65 2.65M5 19l4.2-.7L19.2 8.29a1.88 1.88 0 000-2.66l-.83-.83a1.88 1.88 0 00-2.66 0L5.7 14.8 5 19z" />
                      </svg>
                      <span>{isMailContentEditing ? "Done" : "Edit"}</span>
                    </button>
                    <button
                      className="mail-update-btn"
                      type="button"
                      onClick={handleUpdateMailContent}
                      disabled={mailUpdating}
                      title="Update mail content"
                      aria-label="Update mail content"
                    >
                      {mailUpdating ? "Updating..." : "Update"}
                    </button>
                  </div>
                  {isMailContentEditing && (
                  <div className="mail-editor-toolbar">
                    <select
                      className="mail-editor-select mail-editor-format"
                      defaultValue="p"
                      onChange={(e) => handleMailFormatBlock(e.target.value)}
                      title="Paragraph style"
                      aria-label="Paragraph style"
                    >
                      <option value="p">Normal</option>
                      <option value="h1">Heading 1</option>
                      <option value="h2">Heading 2</option>
                      <option value="h3">Heading 3</option>
                      <option value="pre">Code</option>
                    </select>
                    <select
                      className="mail-editor-select mail-editor-font"
                      defaultValue="Arial"
                      onChange={(e) => handleMailFormatCommand("fontName", e.target.value)}
                      title="Font family"
                      aria-label="Font family"
                    >
                      {MAIL_FONT_FAMILIES.map((font) => (
                        <option key={font} value={font}>{font}</option>
                      ))}
                    </select>
                    <select
                      className="mail-editor-select mail-editor-size"
                      defaultValue=""
                      onChange={(e) => handleMailFormatCommand("fontSize", e.target.value)}
                      title="Font size"
                      aria-label="Font size"
                    >
                      <option value="" disabled>(Font Size)</option>
                      {MAIL_FONT_SIZES.map((size) => (
                        <option key={size.value} value={size.value}>{size.label}</option>
                      ))}
                    </select>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("bold")} title="Bold" aria-label="Bold">B</button>
                    <button type="button" className="mail-editor-btn mail-editor-italic" onClick={() => handleMailFormatCommand("italic")} title="Italic" aria-label="Italic">I</button>
                    <button type="button" className="mail-editor-btn mail-editor-underline" onClick={() => handleMailFormatCommand("underline")} title="Underline" aria-label="Underline">U</button>
                    <button type="button" className="mail-editor-btn mail-editor-strike" onClick={() => handleMailFormatCommand("strikeThrough")} title="Strikethrough" aria-label="Strikethrough">ab</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyLeft")} title="Align left" aria-label="Align left">L</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyCenter")} title="Align center" aria-label="Align center">C</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyRight")} title="Align right" aria-label="Align right">R</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyFull")} title="Justify" aria-label="Justify">J</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("insertUnorderedList")} title="Bullet list" aria-label="Bullet list">List</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("insertOrderedList")} title="Numbered list" aria-label="Numbered list">1.</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("outdent")} title="Decrease indent" aria-label="Decrease indent">&lt;</button>
                    <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("indent")} title="Increase indent" aria-label="Increase indent">&gt;</button>
                    <button
                      type="button"
                      className="mail-editor-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleMailCreateLink}
                      disabled={mailLinkInserting}
                      title="Insert module hyperlink"
                      aria-label="Insert module hyperlink"
                    >
                      {mailLinkInserting ? "Inserting..." : "Insert"}
                    </button>
                    <label className="mail-editor-color" title="Text color" aria-label="Text color">
                      <span>A</span>
                      <input type="color" defaultValue={MAIL_TEXT_COLORS[0]} onChange={(e) => handleMailFormatCommand("foreColor", e.target.value)} />
                    </label>
                    <select
                      className="mail-editor-select mail-editor-color-select"
                      defaultValue=""
                      onChange={(e) => handleMailFormatCommand("foreColor", e.target.value)}
                      title="Text color presets"
                      aria-label="Text color presets"
                    >
                      <option value="" disabled>Text Color</option>
                      {MAIL_TEXT_COLORS.map((color) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                    <label className="mail-editor-color" title="Highlight color" aria-label="Highlight color">
                      <span>H</span>
                      <input type="color" defaultValue={MAIL_HIGHLIGHT_COLORS[1]} onChange={(e) => handleMailFormatCommand("hiliteColor", e.target.value)} />
                    </label>
                    <select
                      className="mail-editor-select mail-editor-color-select"
                      defaultValue=""
                      onChange={(e) => handleMailFormatCommand("hiliteColor", e.target.value)}
                      title="Highlight presets"
                      aria-label="Highlight presets"
                    >
                      <option value="" disabled>Highlight</option>
                      {MAIL_HIGHLIGHT_COLORS.map((color) => (
                        <option key={color} value={color}>{color}</option>
                      ))}
                    </select>
                    <button type="button" className="mail-editor-btn" onClick={handleMailClearFormat} title="Clear formatting" aria-label="Clear formatting">Tx</button>
                  </div>
                  )}
                  <div
                    key={selectedMail.id}
                    ref={mailBodyEditorRef}
                    className={`mail-read-body mail-rich-editor ${isMailContentEditing ? "mail-rich-editor-editing" : "mail-rich-editor-readonly"}`}
                    contentEditable={isMailContentEditing}
                    suppressContentEditableWarning
                    onMouseUp={saveMailEditorSelection}
                    onKeyUp={saveMailEditorSelection}
                    onInput={(e) => {
                      saveMailEditorSelection();
                      handleMailBodyEdit(e.currentTarget.innerHTML);
                    }}
                  />
                  </div>
                  {selectedMail.fileUrl && (
                    <div className="mail-inline-pdf">
                      <div className="mail-inline-pdf-header">
                        <strong>PDF Preview</strong>
                        <span>{selectedMail.fileName || "Attachment.pdf"}</span>
                      </div>
                      <iframe
                        className="mail-inline-pdf-frame"
                        src={selectedMail.fileUrl}
                        title={selectedMail.fileName || "PDF preview"}
                      />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="mail-detail-empty">
                <h3>Select a mail</h3>
                <p>Choose a message from the left list to review, edit content, preview PDF, and send.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )}

  {selectedTask && (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-5xl overflow-hidden bg-white shadow-2xl rounded-2xl animate-fadeIn">
        {/* 🔷 HEADER */}
        <div className="flex items-center justify-between px-5 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
          <h3 className="text-lg font-semibold tracking-wide">{selectedTask.Title}</h3>
          <button
            onClick={() => setOpenTaskId(null)}
            className="text-white transition hover:scale-110"
          >
            ✕
          </button>
        </div>

        {/* 🔹 BODY */}
        <div className="grid gap-4 p-5 lg:grid-cols-[0.72fr_1.28fr]">
          {/* Description */}
          <div className="order-2 p-3 border rounded-lg bg-gray-50 lg:col-start-2">
            <div className="mb-1 text-sm font-semibold text-gray-600">Description</div>
            <div className="text-sm text-gray-800">{selectedTask.Description}</div>
          </div>

          {/* Details Grid */}
          <div className="grid order-3 grid-cols-2 gap-3 text-sm lg:col-start-2 xl:grid-cols-4">
            <div className="p-2 border rounded-lg bg-blue-50">
              <div className="text-xs text-gray-500">Priority</div>
              <div className="font-semibold text-blue-700">{selectedTask.Priority}</div>
            </div>
            <div className="p-2 border rounded-lg bg-purple-50">
              <div className="text-xs text-gray-500">Status</div>
              <div className="font-semibold text-purple-700">{selectedTask.Status}</div>
            </div>
            <div className="p-2 border rounded-lg bg-green-50">
              <div className="text-xs text-gray-500">Start Date</div>
              <div className="font-semibold">
                {new Date(selectedTask.Start_date).toLocaleDateString("en-GB")}
              </div>
            </div>
            <div className="p-2 border rounded-lg bg-red-50">
              <div className="text-xs text-gray-500">Due Date</div>
              <div className="font-semibold text-red-600">
                {new Date(selectedTask.Due_date).toLocaleDateString("en-GB")}
              </div>
            </div>
          </div>

          <div className="grid order-1 gap-4 lg:contents">
          {/* 🔹 ERP LINKS TABLE */}
          <div className="order-5 overflow-hidden border border-blue-100 shadow-sm rounded-xl bg-blue-50/40 lg:col-start-2">
            <div className="px-4 py-3">
              <h4 className="text-sm font-semibold text-blue-900">Linked Documents</h4>
              <div className="text-xs text-blue-500">Related modules and document references</div>
            </div>
            <div className="mx-4 mb-4 overflow-hidden bg-white border border-blue-100 rounded-lg">
              <div className="grid grid-cols-2 px-3 py-2 text-xs font-semibold text-white bg-blue-700">
                <div>Module</div>
                <div>Document</div>
              </div>
              <div className="overflow-y-auto text-sm max-h-64">
                {getLinksByTask(selectedTask.TaskId).map((link) => (
                  <div
                    key={link.LinkId}
                    className="grid grid-cols-2 px-3 py-2 transition border-b border-blue-50 hover:bg-blue-50 gap-y-1"
                  >
                    <div>
                      <div className="font-medium text-blue-700">{link.Module}</div>
                      <div className="text-xs text-gray-500 truncate">{link.MdlId}</div>
                    </div>
                   <div className="text-gray-700 break-words whitespace-normal">
  {link.Document}
</div>
                  </div>
                ))}
                {getLinksByTask(selectedTask.TaskId).length === 0 && (
                  <div className="p-3 text-center text-gray-400">No linked documents</div>
                )}
              </div>
            </div>
          </div>

          <div className="order-1 overflow-visible border shadow-sm border-slate-200 rounded-xl bg-slate-50/80 lg:row-span-3">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">Task Log</div>
                <div className="text-xs text-slate-500">Recent notes and status updates</div>
              </div>
              <button
  type="button"
  onClick={() => {
    setShowTaskLogForm((value) => !value);
    setTaskLogMessage(null);
  }}
  className="flex items-center justify-center text-xl font-semibold leading-none text-white transition bg-blue-600 rounded-full shadow-md h-9 w-9 shadow-blue-200 hover:bg-blue-700 hover:shadow-blue-300"
  aria-label={showTaskLogForm ? "Hide task log form" : "Add task log"}
  title={showTaskLogForm ? "Hide task log form" : "Add task log"}
>
  {showTaskLogForm ? "-" : "+"}
</button>
              {/* <button
                type="button"
                onClick={() => {
                  if (selectedTaskLogs.length > 0) return;
                  setShowTaskLogForm((value) => !value);
                  setTaskLogMessage(null);
                }}
                disabled={selectedTaskLogs.length > 0}
                className="flex items-center justify-center text-xl font-semibold leading-none text-white transition bg-blue-600 rounded-full shadow-md h-9 w-9 shadow-blue-200 hover:bg-blue-700 hover:shadow-blue-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
                aria-label={selectedTaskLogs.length > 0 ? "Task log already exists" : showTaskLogForm ? "Hide task log form" : "Add task log"}
                title={selectedTaskLogs.length > 0 ? "Task log already exists" : showTaskLogForm ? "Hide task log form" : "Add task log"}
              >
                {showTaskLogForm ? "-" : "+"}
              </button> */}
            </div>

            <div className="px-4 py-4 bg-white border-t">
              <div className="pr-1 space-y-3 overflow-y-auto max-h-48">
                {selectedTaskLogs.map((log) => (
                  <div
                    key={log.TlogId}
                    className="p-3 transition bg-white border shadow-sm rounded-xl border-slate-200 hover:border-blue-200 hover:bg-blue-50/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="mb-2 text-sm font-semibold break-words text-slate-800">
                          {log.Notes || "-"}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 font-semibold text-purple-700">
                            {log.Status || "-"}
                          </span>
                          <span>{formatTaskLogTime(log.UpdateTime)}</span>
                          {/* <span className="font-semibold text-slate-600">{log.UpdateBy || "-"}</span> */}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteTaskLog(log)}
                        disabled={taskLogDeletingId === log.TlogId}
                        className="flex items-center justify-center w-8 h-8 text-red-600 transition border border-red-100 rounded-full shrink-0 bg-red-50 hover:border-red-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                        title="Delete task log"
                        aria-label="Delete task log"
                      >
                        {taskLogDeletingId === log.TlogId ? (
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
                        ) : (
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M19 6l-1 14H6L5 6" />
                            <path d="M10 11v5" />
                            <path d="M14 11v5" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
                {selectedTaskLogs.length === 0 && (
                  <div className="px-3 py-5 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
                    No task logs available
                  </div>
                )}
              </div>
            </div>

            {showTaskLogForm && (
              <div className="px-4 py-4 bg-white border-t">
                <div className="p-4 border shadow-sm rounded-xl border-slate-200 bg-slate-50/70">
                <div className="grid gap-4">
                  <label className="block">
                    <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-slate-500">Notes</span>
                    <textarea
                      value={taskLogNotes}
                      onChange={(event) => setTaskLogNotes(event.target.value)}
                      rows={3}
                      className="min-h-[88px] w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      placeholder="Type the task update..."
                    />
                  </label>
                  <label className="block">
                    <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-slate-500">Status</span>
                    <CustomDropdown
                      options={taskStatusOptions.length > 0 ? taskStatusOptions : [{ Status: selectedTask.Status }]}
                      valueKey="Status"
                      labelKey="Status"
                      value={taskLogStatus}
                      onChange={setTaskLogStatus}
                      placeholder="Select status"
                      openDirection="up"
                    />
                  </label>
                </div>

                {taskLogMessage && (
                  <div
                    className={`mt-3 rounded-lg px-3 py-2 text-sm font-medium ${
                      taskLogMessage.type === "success"
                        ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {taskLogMessage.text}
                  </div>
                )}

                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={handleSaveTaskLog}
                    disabled={taskLogSaving}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
>
                    {taskLogSaving ? "Adding..." : "Add"}
                  </button>
                </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* 🔻 FOOTER */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          
       <button
  onClick={() => handleMarkAsRead(selectedTask.TaskId)}
  className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
>
  Read
</button>
        </div>
      </div>
    </div>
  )}

  {showLogoutMailConfirm && (
  <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="w-[420px] max-w-[90vw] rounded-2xl bg-white shadow-2xl border border-blue-100 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
  <div>
    <h3 className="text-lg font-bold">Pending Outbox Mails</h3>
    <p className="text-sm text-blue-100">Before you logout</p>
  </div>

  <button
    type="button"
    className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/20"
    title="Close (Esc)"
    onClick={() => {
      setShowLogoutMailConfirm(false);
      proceedLogout();
    }}
  >
    ✕
  </button>
</div>

      <div className="p-5">
        <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-blue-50">
          <div className="flex items-center justify-center text-white bg-blue-600 rounded-full h-11 w-11">
            ✉
          </div>
          <div>
            <div className="font-bold text-slate-800">
              {logoutOutboxCount} mail(s) in your outbox
            </div>
            <div className="text-sm text-slate-500">
              Do you want to open mail popup and send them now?
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-5 py-2 text-sm font-bold border rounded-full border-slate-300 text-slate-600 hover:bg-slate-100"
            onClick={() => {
              setShowLogoutMailConfirm(false);
              proceedLogout();
            }}
          >
            No, Logout
          </button>

          <button
            type="button"
            className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700"
            onClick={() => {
              setShowLogoutMailConfirm(false);
              setMailFolder("outbox");
              setSelectedMail(null);
              setMailSendMessage(null);
              setIsMailOpen(true);
            }}
          >
            Yes, Open Mail
          </button>
        </div>
      </div>
    </div>
  </div>
)}

  </>
);
}




// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import { useNavigate } from 'react-router-dom';
// import api from '../api/api';
// import { getPermissions } from '../api/configHelper';

// const ITEMS_PER_PAGE = 10;
// const MAIL_FONT_FAMILIES = ["Arial", "Calibri", "Times New Roman", "Georgia", "Verdana", "Tahoma", "Courier New"];
// const MAIL_FONT_SIZES = [
//   { label: "10", value: "1" },
//   { label: "12", value: "2" },
//   { label: "14", value: "3" },
//   { label: "18", value: "4" },
//   { label: "24", value: "5" },
//   { label: "32", value: "6" },
//   { label: "48", value: "7" }
// ];
// const MAIL_TEXT_COLORS = ["#0f172a", "#1d4ed8", "#047857", "#b91c1c", "#c2410c", "#7c3aed", "#000000"];
// const MAIL_HIGHLIGHT_COLORS = ["#ffffff", "#fef08a", "#bfdbfe", "#bbf7d0", "#fecaca", "#e9d5ff", "#fed7aa"];

// const highlightDropdownText = (text, query) => {
//   const value = String(text || "");
//   const search = String(query || "").trim();
//   if (!search) return value;

//   const index = value.toLowerCase().indexOf(search.toLowerCase());
//   if (index === -1) return value;

//   return (
//     <>
//       {value.slice(0, index)}
//       <span className="font-semibold text-blue-700">{value.slice(index, index + search.length)}</span>
//       {value.slice(index + search.length)}
//     </>
//   );
// };

// const CustomDropdown = ({
//   options = [],
//   valueKey,
//   labelKey,
//   value,
//   onChange,
//   placeholder = "Select...",
//   disabled = false,
//   openDirection = "down"
// }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [inputValue, setInputValue] = useState("");
//   const [activeIndex, setActiveIndex] = useState(-1);
//   const wrapperRef = useRef(null);
//   const listRef = useRef(null);

//   useEffect(() => {
//     const selected = options.find(
//       (option) => option?.[valueKey]?.toString() === value?.toString()
//     );
//     setInputValue(selected ? selected[labelKey] : "");
//   }, [value, options, valueKey, labelKey]);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
//         setIsOpen(false);
//         setActiveIndex(-1);
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const filteredOptions = options.filter((option) =>
//     String(option?.[labelKey] || "").toLowerCase().includes(inputValue.toLowerCase())
//   );

//   useEffect(() => {
//     if (listRef.current && activeIndex >= 0) {
//       listRef.current.children[activeIndex]?.scrollIntoView({ block: "nearest" });
//     }
//   }, [activeIndex]);

//   const selectOption = (option) => {
//     onChange(option[valueKey]);
//     setInputValue(option[labelKey]);
//     setIsOpen(false);
//     setActiveIndex(-1);
//   };

//   const handleKeyDown = (event) => {
//     if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
//       setIsOpen(true);
//       setActiveIndex(0);
//       return;
//     }

//     if (event.key === "ArrowDown") {
//       event.preventDefault();
//       setActiveIndex((index) => (index < filteredOptions.length - 1 ? index + 1 : 0));
//     } else if (event.key === "ArrowUp") {
//       event.preventDefault();
//       setActiveIndex((index) => (index > 0 ? index - 1 : filteredOptions.length - 1));
//     } else if (event.key === "Enter" && isOpen && activeIndex >= 0) {
//       event.preventDefault();
//       const selected = filteredOptions[activeIndex];
//       if (selected) selectOption(selected);
//     } else if (event.key === "Escape") {
//       setIsOpen(false);
//       setActiveIndex(-1);
//     }
//   };

//   return (
//     <div ref={wrapperRef} className="relative w-full">
//       <input
//         type="text"
//         className="w-full h-10 px-3 py-2 text-sm font-semibold transition bg-white border rounded-lg outline-none border-slate-300 pr-9 text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
//         placeholder={placeholder}
//         value={inputValue}
//         disabled={disabled}
//         onFocus={() => !disabled && setIsOpen(true)}
//         onChange={(event) => {
//           setInputValue(event.target.value);
//           setIsOpen(true);
//           setActiveIndex(-1);
//         }}
//         onKeyDown={handleKeyDown}
//       />
//       <span className="absolute -translate-y-1/2 pointer-events-none right-3 top-1/2 text-slate-400">
//         ▾
//       </span>

//       {isOpen && !disabled && (
//         <div
//           ref={listRef}
//           className={`absolute z-[9999] max-h-52 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl ${
//             openDirection === "up" ? "bottom-full mb-1" : "mt-1"
//           }`}
//         >
//           {filteredOptions.length ? (
//             filteredOptions.map((option, index) => (
//               <div
//                 key={option[valueKey]}
//                 className={`cursor-pointer px-3 py-2 text-sm text-slate-700 ${
//                   index === activeIndex ? "bg-blue-50 font-semibold" : "hover:bg-slate-50"
//                 }`}
//                 onMouseEnter={() => setActiveIndex(index)}
//                 onMouseDown={() => selectOption(option)}
//               >
//                 {highlightDropdownText(option[labelKey], inputValue)}
//               </div>
//             ))
//           ) : (
//             <div className="px-3 py-2 text-sm text-slate-400">No results found</div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// export default function Header({ onToggleSidebar, headerActions }) {
//   const [companyDetails, setCompanyDetails] = useState(null);
//   const [currentTime, setCurrentTime] = useState(new Date());
//   const [scrolled, setScrolled] = useState(false);
//   const [userName, setUserName] = useState("");
//   const [isPopupOpen, setIsPopupOpen] = useState(false);
//   const [isResetModalOpen, setIsResetModalOpen] = useState(false);
//   const [emailId, setEmailId] = useState("");
//   const [newPassword, setNewPassword] = useState("");
//   const [resetLoading, setResetLoading] = useState(false);
//   const [resetMessage, setResetMessage] = useState(null);
//   const [resetStep, setResetStep] = useState(1); // 1: enter details, 2: enter OTP
//   const [otp, setOtp] = useState("");
//   const [permissions, setPermissions] = useState({});
//   const popupRef = useRef();
//   const [taskCount, setTaskCount] = useState(0);
//   const [taskList, setTaskList] = useState([]);
//   const [showNotice, setShowNotice] = useState(false);
//   const [openTaskId, setOpenTaskId] = useState(null);
//   const [taskStatusOptions, setTaskStatusOptions] = useState([]);
//   const [taskLogs, setTaskLogs] = useState([]);
//   const [showTaskLogForm, setShowTaskLogForm] = useState(false);
//   const [taskLogNotes, setTaskLogNotes] = useState("");
//   const [taskLogStatus, setTaskLogStatus] = useState("");
//   const [taskLogSaving, setTaskLogSaving] = useState(false);
//   const [taskLogDeletingId, setTaskLogDeletingId] = useState(null);
//   const [taskLogMessage, setTaskLogMessage] = useState(null);
//   const [noticeSelectedDate, setNoticeSelectedDate] = useState(new Date());
//   const [noticeMonth, setNoticeMonth] = useState(new Date());
//   const [noticeFromDate, setNoticeFromDate] = useState(() => {
//     const date = new Date();
//     return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
//   });
//   const [noticeToDate, setNoticeToDate] = useState(() => {
//     const date = new Date();
//     return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
//   });
//   const [appliedNoticeFromDate, setAppliedNoticeFromDate] = useState(() => {
//     const date = new Date();
//     return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
//   });
//   const [appliedNoticeToDate, setAppliedNoticeToDate] = useState(() => {
//     const date = new Date();
//     return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
//   });
//   const [currentPage, setCurrentPage] = useState(1);
//   const [isMailOpen, setIsMailOpen] = useState(false);
//   const [selectedMail, setSelectedMail] = useState(null);
//   const [mailSearch, setMailSearch] = useState("");
//   const [mailFolder, setMailFolder] = useState("outbox");
//   const [mailItems, setMailItems] = useState([]);
//   const [mailLoading, setMailLoading] = useState(false);
//   const [mailError, setMailError] = useState("");
//   const [mailSending, setMailSending] = useState(false);
//   const [mailSendingAll, setMailSendingAll] = useState(false);
//   const [mailUpdating, setMailUpdating] = useState(false);
//   const [mailLinkInserting, setMailLinkInserting] = useState(false);
//   const [mailDeletingLogId, setMailDeletingLogId] = useState(null);
//   const [vendorToLoading, setVendorToLoading] = useState(false);
//   const [mailSendMessage, setMailSendMessage] = useState(null);
//   const [mailPopupSize, setMailPopupSize] = useState("normal");
//   const [isMailContentEditing, setIsMailContentEditing] = useState(false);
//   const [isMailToEditing, setIsMailToEditing] = useState(false);
//   const [isMailListCollapsed, setIsMailListCollapsed] = useState(false);
//   const [mailPopupOffset, setMailPopupOffset] = useState({ x: 0, y: 0 });
//   const [isMailDragging, setIsMailDragging] = useState(false);
//   const mailBodyEditorRef = useRef(null);
//   const mailBodyLoadedIdRef = useRef(null);
//   const mailSelectionRef = useRef(null);
//   const mailDragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
//   const mailSendAllStopRef = useRef(false);
//   const taskLogOpenTaskRef = useRef(null);

//   const [taskLinks, setTaskLinks] = useState([]);
//   const [approvalCount, setApprovalCount] = useState(0);
//   const [noticeSearch, setNoticeSearch] = useState("");
//   const navigate = useNavigate();
// const [showLogoutMailConfirm, setShowLogoutMailConfirm] = useState(false);
// const [logoutOutboxCount, setLogoutOutboxCount] = useState(0);
// const AUTH_STORAGE_KEYS = ["AccessToken", "RefreshToken", "CurrentAuthToken", "AuthToken", "userId", "userName", "UserInfo", "LogId"];

//   useEffect(() => {
//   loadTasks();
//   loadApprovalCount();
// }, []);

// useEffect(() => {
//   if (!showNotice) return undefined;

//   const handleNoticeEscape = (event) => {
//     if (event.key === "Escape") {
//       if (openTaskId) {
//         setOpenTaskId(null);
//         return;
//       }

//       setShowNotice(false);
//     }
//   };

//   window.addEventListener("keydown", handleNoticeEscape);
//   return () => window.removeEventListener("keydown", handleNoticeEscape);
// }, [showNotice, openTaskId]);


// const navigateToApproval = () => {
//   const allMenus = JSON.parse(sessionStorage.getItem("allSubMenuList"));

//   const list = allMenus?.Table || [];

//   // 🔍 find Approval module
//   const approvalMenu = list.find(
//     (x) => x.MdlId === "Approval" || x.MdlDisplayName === "Approval"
//   );

//   if (approvalMenu?.ReactWebPath) {
//     let url = `${approvalMenu.ReactWebPath}?mdlId=${approvalMenu.MdlId}`;

//     if (approvalMenu.TxnType) {
//       url += `&TxnType=${encodeURIComponent(approvalMenu.TxnType)}`;
//     }

//     if (approvalMenu.TxnSubType) {
//       url += `&TxnSubType=${encodeURIComponent(approvalMenu.TxnSubType)}`;
//     }

//     window.location.href = url;
//   } else {
//     console.warn("Approval menu not found");
//   }
// };


// const loadApprovalCount = async () => {
//   try {
//     const userId = sessionStorage.getItem("userId");

//     const res = await api.GetApprovalCount(userId);
   
//     // ✅ FIXED PATH
//     const count = res?.Table?.[0]?.PendingCount || 0;

//     setApprovalCount(count);
//   } catch (err) {
//     console.error("Approval count error", err);
//   }
// };

// const getMailName = (email) => {
//   if (!email) return "Mail";
//   return String(email).split("@")[0] || email;
// };

// const loadMailOutbox = useCallback(async () => {
//   setMailLoading(true);
//   setMailError("");

//   try {
//     const currentUserName = String(sessionStorage.getItem("userName") || "")
//       .trim()
//       .toLowerCase();

//     const res = await api.GetMailOutbox();

//     const rows = res?.data?.Table || res?.Data?.Table || res?.Table || [];

//     const currentUserRows = rows.filter((row) => {
//       const rowUserName = String(row.Username || row.UserName || row.userName || "")
//         .trim()
//         .toLowerCase();

//       return rowUserName === currentUserName;
//     });

//     setMailItems(
//       currentUserRows.map((row, index) => {
//         const generatedAt =
//           row.MailTime || row.GenerateTime || row.GeneratedTime || new Date().toISOString();

//         const fileName = row.FileName || "";
//         const fileUrl = row.FileUrl || "";

//         return {
//           id: row.logId ?? row.LogId ?? row.PrimeId ?? index,
//           logId: row.logId ?? row.LogId ?? null,
//           username: row.Username || row.UserName || row.userName || "",
//           senderName: getMailName(row.FromEMailid || row.FromEmailId),
//           from: row.FromEMailid || row.FromEmailId || "",
//           to: row.ToEMailid || row.ToEmailId || "",
//           subject: row.Subject || "(No subject)",
//           preview: "",
//           attachmentCount: fileName ? 1 : 0,
//           status: Number(row.MailStatus ?? 0),
//           generatedAt: new Date(generatedAt),
//           body: row.MailContent || "",
//           mdlId: row.Mdlid || row.MdlId || row.mdlId || "",
//           primeId: row.PrimeId ?? row.Primeid ?? row.primeId ?? "",
//           seqId:
//             row.SeqId ??
//             row.Seqid ??
//             row.seqId ??
//             String(row.MailContent || "").match(/mdldirect=[^!&"']*!([^!&"']+)!/i)?.[1] ??
//             "",
//           fileName,
//           fileUrl
//         };
//       })
//     );
//   } catch (err) {
//     console.error("Mail outbox load error", err);
//     setMailItems([]);
//     setMailError("Unable to load mail outbox");
//   } finally {
//     setMailLoading(false);
//   }
// }, []);

// useEffect(() => {
//   loadMailOutbox();
// }, [loadMailOutbox]);

// useEffect(() => {
//   if (!mailSendMessage) return undefined;

//   const timer = window.setTimeout(() => {
//     setMailSendMessage(null);
//   }, 3000);

//   return () => window.clearTimeout(timer);
// }, [mailSendMessage]);

// useEffect(() => {
//   if (!isMailDragging) return undefined;

//   const handleMailDragMove = (event) => {
//     const nextX = mailDragStartRef.current.offsetX + event.clientX - mailDragStartRef.current.x;
//     const nextY = mailDragStartRef.current.offsetY + event.clientY - mailDragStartRef.current.y;
//     setMailPopupOffset({ x: nextX, y: nextY });
//   };

//   const handleMailDragEnd = () => {
//     setIsMailDragging(false);
//   };

//   window.addEventListener("mousemove", handleMailDragMove);
//   window.addEventListener("mouseup", handleMailDragEnd);

//   return () => {
//     window.removeEventListener("mousemove", handleMailDragMove);
//     window.removeEventListener("mouseup", handleMailDragEnd);
//   };
// }, [isMailDragging]);

// useEffect(() => {
//   if (!isMailOpen) return undefined;

//   const handleMailEscape = (event) => {
//     if (event.key === "Escape") {
//       setIsMailOpen(false);
//       setSelectedMail(null);
//       setMailSendMessage(null);
//     }
//   };

//   window.addEventListener("keydown", handleMailEscape);
//   return () => window.removeEventListener("keydown", handleMailEscape);
// }, [isMailOpen]);

// useEffect(() => {
//   if (!selectedMail) {
//     mailBodyLoadedIdRef.current = null;
//     mailSelectionRef.current = null;
//     setIsMailContentEditing(false);
//     setIsMailToEditing(false);
//     return;
//   }

//   if (mailBodyEditorRef.current && mailBodyLoadedIdRef.current !== selectedMail.id) {
//     mailBodyEditorRef.current.innerHTML = selectedMail.body || "";
//     mailBodyLoadedIdRef.current = selectedMail.id;
//     mailSelectionRef.current = null;
//     setIsMailContentEditing(false);
//     setIsMailToEditing(false);
//   }
// }, [selectedMail]);

// const handleSendMail = async () => {
//   if (!selectedMail?.logId) {
//     setMailSendMessage({ type: "error", text: "Select a mail to send" });
//     return;
//   }

//   setMailSending(true);
//   setMailSendMessage(null);
//   try {
//     const result = await api.SendMailByLogId(selectedMail.logId);
//     const message = result?.message || result?.Message || "Mail sent successfully";
//     setMailSendMessage({ type: "success", text: message });
//     await loadMailOutbox();
//   } catch (err) {
//     console.error("Send mail error", err);
//     setMailSendMessage({ type: "error", text: err.message || "Unable to send mail" });
//   } finally {
//     setMailSending(false);
//   }
// };

// const handleSendAllMail = async () => {
//   const currentUserName = String(sessionStorage.getItem("userName") || "")
//     .trim()
//     .toLowerCase();

//   const pendingMails = mailItems.filter((mail) =>
//     mail.status === 0 &&
//     mail.logId &&
//     String(mail.username || "").trim().toLowerCase() === currentUserName
//   );

//   if (pendingMails.length === 0) {
//     setMailSendMessage({
//       type: "error",
//       text: "No pending mails available for this user"
//     });
//     return;
//   }

//   mailSendAllStopRef.current = false;
//   setMailSendingAll(true);
//   setMailSendMessage({
//     type: "success",
//     text: `Sending 0 of ${pendingMails.length} mails...`
//   });

//   let sentCount = 0;
//   let failedCount = 0;

//   try {
//     for (const mail of pendingMails) {
//       if (mailSendAllStopRef.current) break;

//       try {
//         const result = await api.SendMailByLogId(mail.logId);
//         const status = result?.Status ?? result?.status;

//         if (status === 1) {
//           sentCount += 1;
//         } else {
//           failedCount += 1;
//         }
//       } catch (err) {
//         failedCount += 1;
//         console.error(`Send all mail error for logId ${mail.logId}`, err);
//       }

//       if (!mailSendAllStopRef.current) {
//         setMailSendMessage({
//           type: failedCount > 0 ? "error" : "success",
//           text: `Sending ${sentCount + failedCount} of ${pendingMails.length} mails...`
//         });
//       }
//     }

//     if (mailSendAllStopRef.current) {
//       setMailSendMessage({
//         type: "error",
//         text: `Sending stopped. ${sentCount} mail(s) sent.`
//       });
//     } else if (failedCount > 0) {
//       setMailSendMessage({
//         type: "error",
//         text: `${sentCount} mail(s) sent, ${failedCount} failed.`
//       });
//     } else {
//       setMailSendMessage({
//         type: "success",
//         text: `${sentCount} mail(s) sent successfully.`
//       });
//     }

//     await loadMailOutbox();
//   } finally {
//     setMailSendingAll(false);
//     mailSendAllStopRef.current = false;
//   }
// };
// const handlePassSendAll = () => {
//   if (!mailSendingAll) return;
//   mailSendAllStopRef.current = true;
//   setMailSendMessage({ type: "error", text: "Stopping mail sending after the current mail..." });
// };

// const handleUpdateMailContent = async () => {
//   if (!selectedMail?.logId) {
//     setMailSendMessage({ type: "error", text: "Select a mail to update" });
//     return;
//   }

//   setMailUpdating(true);
//   setMailSendMessage(null);
//   try {
//     const result = await api.UpdateMailContent(selectedMail.logId, selectedMail.body || "", selectedMail.to || "");
//     const message = result?.message || result?.Message || "Mail content updated successfully";
//     setMailSendMessage({ type: "success", text: message });
//     await loadMailOutbox();
//   } catch (err) {
//     console.error("Update mail content error", err);
//     setMailSendMessage({ type: "error", text: err.message || "Unable to update mail content" });
//   } finally {
//     setMailUpdating(false);
//   }
// };

// const handleDeleteMail = async (mail, event) => {
//   event.stopPropagation();

//   if (!mail?.logId) {
//     setMailSendMessage({ type: "error", text: "Select a mail to delete" });
//     return;
//   }

//   setMailDeletingLogId(mail.logId);
//   setMailSendMessage(null);
//   try {
//     const result = await api.DeleteMailOutBox(mail.logId);
//     const message = result?.message || result?.Message || "Mail deleted successfully.";
//     setMailSendMessage({ type: "success", text: message });
//     if (selectedMail?.id === mail.id) {
//       setSelectedMail(null);
//     }
//     await loadMailOutbox();
//   } catch (err) {
//     console.error("Delete mail error", err);
//     setMailSendMessage({ type: "error", text: err.message || "Unable to delete mail" });
//   } finally {
//     setMailDeletingLogId(null);
//   }
// };

// const handleMailBodyEdit = (html) => {
//   if (!selectedMail) return;

//   setSelectedMail((prev) => prev ? { ...prev, body: html } : prev);
//   setMailItems((prev) =>
//     prev.map((mail) =>
//       mail.id === selectedMail.id ? { ...mail, body: html } : mail
//     )
//   );
// };

// const handleMailToEdit = (toEMailid) => {
//   if (!selectedMail) return;

//   setSelectedMail((prev) => prev ? { ...prev, to: toEMailid } : prev);
//   setMailItems((prev) =>
//     prev.map((mail) =>
//       mail.id === selectedMail.id ? { ...mail, to: toEMailid } : mail
//     )
//   );
// };

// const handleFetchVendorToAddress = async () => {
//   if (!selectedMail?.logId) {
//     setMailSendMessage({ type: "error", text: "Select a mail to fetch vendor address" });
//     return;
//   }

//   setVendorToLoading(true);
//   setMailSendMessage(null);
//   try {
//     const result = await api.GetVendorToAddress(selectedMail.logId);
//     const toEMailid =
//       result?.data?.Table?.[0]?.ToEMailid ||
//       result?.Data?.Table?.[0]?.ToEMailid ||
//       result?.Table?.[0]?.ToEMailid ||
//       "";

//     if (!toEMailid) {
//       setMailSendMessage({ type: "error", text: "Vendor mail address not found" });
//       return;
//     }

//     handleMailToEdit(toEMailid);
//     setMailSendMessage({ type: "success", text: result?.message || result?.Message || "Vendor mail address fetched" });
//   } catch (err) {
//     console.error("Vendor mail address fetch error", err);
//     setMailSendMessage({ type: "error", text: err.message || "Unable to fetch vendor mail address" });
//   } finally {
//     setVendorToLoading(false);
//   }
// };

// const syncMailEditorBody = () => {
//   if (!mailBodyEditorRef.current) return;
//   handleMailBodyEdit(mailBodyEditorRef.current.innerHTML);
// };

// const saveMailEditorSelection = () => {
//   const editor = mailBodyEditorRef.current;
//   const selection = window.getSelection();
//   if (!editor || !selection || selection.rangeCount === 0) return;

//   const range = selection.getRangeAt(0);
//   if (editor.contains(range.commonAncestorContainer)) {
//     mailSelectionRef.current = range.cloneRange();
//   }
// };

// const restoreMailEditorSelection = () => {
//   const selection = window.getSelection();
//   const range = mailSelectionRef.current;
//   if (!selection || !range) return;

//   selection.removeAllRanges();
//   selection.addRange(range);
// };

// const handleMailFormatCommand = (command, value = null) => {
//   if (!selectedMail || !mailBodyEditorRef.current) return;

//   mailBodyEditorRef.current.focus();
//   restoreMailEditorSelection();
//   document.execCommand(command, false, value);
//   saveMailEditorSelection();
//   syncMailEditorBody();
// };

// const handleMailFormatBlock = (value) => {
//   handleMailFormatCommand("formatBlock", value);
// };

// const handleMailCreateLink = async () => {
//   const editor = mailBodyEditorRef.current;
//   const range = mailSelectionRef.current;

//   if (!selectedMail || !editor || !range || range.collapsed || !editor.contains(range.commonAncestorContainer)) {
//     setMailSendMessage({ type: "error", text: "Select the mail content that should become a hyperlink." });
//     return;
//   }

//   if (!selectedMail.mdlId || selectedMail.primeId === "" || selectedMail.primeId == null) {
//     setMailSendMessage({ type: "error", text: "Module or document id was not found for this mail." });
//     return;
//   }

//   setMailLinkInserting(true);
//   setMailSendMessage(null);
//   try {
//     let moduleSeqId = selectedMail.seqId;

//     if (!moduleSeqId) {
//       const cachedMenus = JSON.parse(sessionStorage.getItem("allSubMenuList") || "{}");
//       const menuRows = cachedMenus?.Table || cachedMenus?.table || [];
//       const moduleMenu = menuRows.find(
//         (menu) => String(menu.MdlId || menu.Mdlid || "").toLowerCase() === String(selectedMail.mdlId).toLowerCase()
//       );
//       moduleSeqId = moduleMenu?.SeqId || moduleMenu?.Seqid || moduleMenu?.seqId;
//     }

//     if (!moduleSeqId) {
//       const moduleRes = await api.GetModuleByMdlId(selectedMail.mdlId);
//       moduleSeqId =
//         moduleRes?.seqId ||
//         moduleRes?.SeqId ||
//         moduleRes?.data?.seqId ||
//         moduleRes?.data?.SeqId ||
//         moduleRes?.Data?.seqId ||
//         moduleRes?.Data?.SeqId;
//     }

//     if (!moduleSeqId) {
//       throw new Error("Module sequence id was not found.");
//     }

//     const databaseKey = sessionStorage.getItem("databaseKey") || "Pgpl2026";
//     const moduleDirect = `0!${encodeURIComponent(moduleSeqId)}!${encodeURIComponent(selectedMail.primeId)}`;
//     const hyperlink = `${window.location.origin}/?dBKEY=${encodeURIComponent(databaseKey)}&mdldirect=${moduleDirect}`;

//     editor.focus();
//     restoreMailEditorSelection();
//     document.execCommand("createLink", false, hyperlink);
//     saveMailEditorSelection();
//     syncMailEditorBody();
//     setMailSendMessage({ type: "success", text: "Hyperlink inserted into the selected mail content." });
//   } catch (err) {
//     console.error("Mail hyperlink insert failed", err);
//     setMailSendMessage({ type: "error", text: err.message || "Unable to insert hyperlink." });
//   } finally {
//     setMailLinkInserting(false);
//   }
// };

// const handleMailClearFormat = () => {
//   handleMailFormatCommand("removeFormat");
// };

// const toggleMailContentEditMode = () => {
//   setIsMailContentEditing((prev) => {
//     const next = !prev;
//     if (!next) {
//       syncMailEditorBody();
//       mailSelectionRef.current = null;
//     } else {
//       window.setTimeout(() => {
//         mailBodyEditorRef.current?.focus();
//       }, 0);
//     }
//     return next;
//   });
// };

// const toggleMailToEditMode = () => {
//   setIsMailToEditing((prev) => !prev);
// };

// const handleMailPopupDragStart = (event) => {
//   if (event.button !== 0) return;
//   if (event.target.closest("button, input, select, textarea, label, a")) return;

//   mailDragStartRef.current = {
//     x: event.clientX,
//     y: event.clientY,
//     offsetX: mailPopupOffset.x,
//     offsetY: mailPopupOffset.y
//   };
//   setIsMailDragging(true);
// };

// const resizeMailPopup = (direction) => {
//   const sizes = ["compact", "normal", "large"];
//   const currentIndex = sizes.indexOf(mailPopupSize);
//   const nextIndex = direction === "in"
//     ? Math.min(currentIndex + 1, sizes.length - 1)
//     : Math.max(currentIndex - 1, 0);

//   setMailPopupSize(sizes[nextIndex]);
// };

// const loadTasks = async () => {
//   try {
//     const userName = sessionStorage.getItem("userName");
//     const res = await api.GetUserTaskList(userName);

//     setTaskList(res?.Table || []);
//     setTaskCount(res?.Table1?.[0]?.TaskCount || 0);
//     const links = res?.taskList?.Table2 ?? res?.Table2 ?? [];
//     setTaskLinks(links);
//     setTaskStatusOptions(res?.taskList?.Table3 ?? res?.Table3 ?? []);
//     setTaskLogs(res?.taskList?.Table4 ?? res?.Table4 ?? []);
//   } catch (err) {
//     console.error("Task load error", err);
//   }
// };




// const getLinksByTask = (taskId) => {
//   if (!taskId) return [];
//   const normalizedId = String(taskId);
//   return taskLinks.filter((link) => String(link.TaskId) === normalizedId);
// };

// const getLogsByTask = (taskId) => {
//   if (!taskId) return [];
//   const normalizedId = String(taskId);
//   return taskLogs.filter((log) => String(log.TaskId) === normalizedId);
// };

// const formatTaskLogTime = (value) => {
//   if (!value) return "-";
//   const date = new Date(value);
//   if (Number.isNaN(date.getTime())) return value;

//   return date.toLocaleString("en-GB", {
//     day: "2-digit",
//     month: "2-digit",
//     year: "numeric",
//     hour: "2-digit",
//     minute: "2-digit"
//   });
// };

// const getDateKey = (value) => {
//   const date = value instanceof Date ? value : new Date(value);
//   if (Number.isNaN(date.getTime())) return "";

//   return [
//     date.getFullYear(),
//     String(date.getMonth() + 1).padStart(2, "0"),
//     String(date.getDate()).padStart(2, "0")
//   ].join("-");
// };

// const getTaskNoticeDateKey = (task) => getDateKey(task?.Due_date || task?.Start_date);
// const noticeSelectedDateKey = getDateKey(noticeSelectedDate);
// const noticeTodayKey = getDateKey(new Date());
// const noticeMonthLabel = noticeMonth.toLocaleDateString("en-US", {
//   month: "long",
//   year: "numeric"
// });
// const noticeSelectedDateLabel = noticeSelectedDate.toLocaleDateString("en-US", {
//   weekday: "short",
//   month: "short",
//   day: "numeric"
// });

// const tasksForNoticeDate = taskList.filter(
//   (task) => getTaskNoticeDateKey(task) === noticeSelectedDateKey
// );
// const tasksForNoticeRange = taskList.filter((task) => {
//   const taskDate = getTaskNoticeDateKey(task);
//   return taskDate && taskDate >= appliedNoticeFromDate && taskDate <= appliedNoticeToDate;
// });
// const noticeTaskCountByDate = taskList.reduce((acc, task) => {
//   const key = getTaskNoticeDateKey(task);
//   if (!key) return acc;
//   acc[key] = (acc[key] || 0) + 1;
//   return acc;
// }, {});

// const filteredNoticeRangeTasks = tasksForNoticeRange.filter((task) => {
//   const q = noticeSearch.trim().toLowerCase();
//   if (!q) return true;

//   return [
//     task.Title,
//     task.Description,
//     task.UserName,
//     task.Priority,
//     task.Status
//   ]
//     .join(" ")
//     .toLowerCase()
//     .includes(q);
// });

// const applyNoticeDateRange = () => {
//   if (!noticeFromDate || !noticeToDate) return;

//   if (noticeFromDate <= noticeToDate) {
//     setAppliedNoticeFromDate(noticeFromDate);
//     setAppliedNoticeToDate(noticeToDate);
//   } else {
//     setNoticeFromDate(noticeToDate);
//     setNoticeToDate(noticeFromDate);
//     setAppliedNoticeFromDate(noticeToDate);
//     setAppliedNoticeToDate(noticeFromDate);
//   }
// };

// const filteredNoticeDateTasks = tasksForNoticeDate.filter((task) => {
//   const q = noticeSearch.trim().toLowerCase();
//   if (!q) return true;

//   return [
//     task.Title,
//     task.Description,
//     task.UserName,
//     task.Priority,
//     task.Status
//   ]
//     .join(" ")
//     .toLowerCase()
//     .includes(q);
// });

// const noticeCalendarDays = (() => {
//   const year = noticeMonth.getFullYear();
//   const month = noticeMonth.getMonth();
//   const firstDay = new Date(year, month, 1);
//   const daysInMonth = new Date(year, month + 1, 0).getDate();
//   const mondayStartOffset = (firstDay.getDay() + 6) % 7;
//   const days = [];

//   for (let index = 0; index < mondayStartOffset; index += 1) {
//     days.push(null);
//   }

//   for (let day = 1; day <= daysInMonth; day += 1) {
//     days.push(new Date(year, month, day));
//   }

//   while (days.length % 7 !== 0) {
//     days.push(null);
//   }

//   return days;
// })();

// const changeNoticeMonth = (offset) => {
//   setNoticeMonth((value) => new Date(value.getFullYear(), value.getMonth() + offset, 1));
// };

// const goToNoticeToday = () => {
//   const today = new Date();
//   setNoticeSelectedDate(today);
//   setNoticeMonth(today);
// };

//   const totalPages = Math.ceil(taskList.length / ITEMS_PER_PAGE);
//   const displayTotalPages = totalPages > 0 ? totalPages : 1;
//   const paginatedTaskList = taskList.slice(
//     (currentPage - 1) * ITEMS_PER_PAGE,
//     currentPage * ITEMS_PER_PAGE
//   );
//   const paginationNeeded = totalPages > 1;

//   useEffect(() => { 
//     if (currentPage > displayTotalPages) {
//       setCurrentPage(displayTotalPages);
//     }
//   }, [currentPage, displayTotalPages]);

//   const goToPage = (page) => {
//     const nextPage = Math.min(Math.max(page, 1), displayTotalPages);
//     if (nextPage === currentPage) return;
//     setCurrentPage(nextPage);
//   };
//   useEffect(() => {
//   const handleScroll = () => {
//     setScrolled(window.scrollY > 10);
//   };
//   window.addEventListener("scroll", handleScroll);
//   return () => window.removeEventListener("scroll", handleScroll);
// }, []);

//     useEffect(() => {
//   const details = sessionStorage.getItem("companyDetails");
//   const name = sessionStorage.getItem("userName");

//   if (details) {
//     setCompanyDetails(JSON.parse(details));
//   } else {
//     navigate("/");
//   }

//   if (name) setUserName(name);
// }, [navigate]);

//   useEffect(() => {
//     const timer = setInterval(() => {
//       setCurrentTime(new Date());
//     }, 1000);
//     return () => clearInterval(timer);
//   }, []);

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (popupRef.current && !popupRef.current.contains(event.target)) {
//         setIsPopupOpen(false);
//       }
//     };
//     if (isPopupOpen) {
//       document.addEventListener('mousedown', handleClickOutside);
//     }
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, [isPopupOpen]);

//   useEffect(() => {
//     const loadPermissions = async () => {
//       try {
//         const perms = await getPermissions();
//         setPermissions(perms);
//       } catch (error) {
//         console.error('Failed to load permissions:', error);
//       }
//     };
//     loadPermissions();
//   }, []);

// const toggleTask = (taskId) => {
//   setOpenTaskId(openTaskId === taskId ? null : taskId);
// };

// const selectedTask = taskList.find((task) => task.TaskId === openTaskId) || null;
// const selectedTaskLogs = selectedTask ? getLogsByTask(selectedTask.TaskId) : [];

// const resetTaskLogForm = useCallback((status = "") => {
//   setShowTaskLogForm(false);
//   setTaskLogNotes("");
//   setTaskLogStatus(status);
//   setTaskLogMessage(null);
//   setTaskLogSaving(false);
//   setTaskLogDeletingId(null);
// }, []);

// useEffect(() => {
//   if (!selectedTask) {
//     taskLogOpenTaskRef.current = null;
//     return;
//   }

//   if (taskLogOpenTaskRef.current === selectedTask.TaskId) return;

//   const fallbackStatus = selectedTask.Status || taskStatusOptions?.[0]?.Status || "";
//   taskLogOpenTaskRef.current = selectedTask.TaskId;
//   resetTaskLogForm(fallbackStatus);
// }, [selectedTask, taskStatusOptions, resetTaskLogForm]);

// const handleSaveTaskLog = async () => {
//   if (!selectedTask || taskLogSaving) return;

//   const notes = taskLogNotes.trim();
//   if (!notes) {
//     setTaskLogMessage({ type: "error", text: "Please enter notes before adding the log." });
//     return;
//   }

//   try {
//     setTaskLogSaving(true);
//     setTaskLogMessage(null);

//     const res = await api.SaveUpdateTaskLog({
//       tlogId: 0,
//       taskId: selectedTask.TaskId,
//       notes,
//       status: taskLogStatus || selectedTask.Status || "",
//       updateBy: sessionStorage.getItem("userName") || userName
//     });

//     setTaskLogNotes("");
//     setTaskLogMessage({
//       type: "success",
//       text: res?.message || "Task log saved successfully."
//     });
//     setShowTaskLogForm(false);

//     await api.MarkTaskAsRead({
//       taskId: selectedTask.TaskId,
//       userName: sessionStorage.getItem("userName") || userName,
//       isRead: 1
//     });

//     await loadTasks();
//   } catch (err) {
//     console.error("Task log save error", err);
//     setTaskLogMessage({
//       type: "error",
//       text: err?.message || "Unable to save task log."
//     });
//   } finally {
//     setTaskLogSaving(false);
//   }
// };

// const handleDeleteTaskLog = async (log) => {
//   if (!log?.TlogId || taskLogDeletingId) return;

//   const confirmed = window.confirm("Delete this task log?");
//   if (!confirmed) return;

//   try {
//     setTaskLogDeletingId(log.TlogId);
//     setTaskLogMessage(null);

//     const res = await api.DeleteTaskLog(log.TlogId);
//     setTaskLogMessage({
//       type: "success",
//       text: res?.message || "Task log deleted successfully."
//     });
//     await loadTasks();
//   } catch (err) {
//     console.error("Task log delete error", err);
//     setTaskLogMessage({
//       type: "error",
//       text: err?.message || "Unable to delete task log."
//     });
//   } finally {
//     setTaskLogDeletingId(null);
//   }
// };

// const handleMarkAsRead = async (taskId) => {
//   const userName = sessionStorage.getItem("userName");

//   try {
//     // ✅ Step 1: Mark as read
//     await api.MarkTaskAsRead({
//       taskId: taskId,
//       userName: userName,
//       isRead: 1
//     });

//     // ✅ Step 2: Reload full data
//     await loadTasks();

//     // ✅ Step 3: Close popup
//     setOpenTaskId(null);
//     resetTaskLogForm();

//   } catch (err) {
//     console.error("Mark read error", err);
//   }
// };

// const filteredMailItems = mailItems.filter((mail) => {
//   const isOutboxMail = mail.status === 0;
//   if (mailFolder === "outbox" && !isOutboxMail) return false;
//   if (mailFolder === "sent" && isOutboxMail) return false;

//   const query = mailSearch.trim().toLowerCase();
//   if (!query) return true;
//   return [mail.from, mail.to, mail.subject, mail.preview]
//     .join(" ")
//     .toLowerCase()
//     .includes(query);
// });
// const selectedMailIndex = selectedMail
//   ? filteredMailItems.findIndex((mail) => mail.id === selectedMail.id)
//   : -1;
// const canOpenPreviousMail = selectedMailIndex > 0;
// const canOpenNextMail = selectedMailIndex >= 0 && selectedMailIndex < filteredMailItems.length - 1;

// const handleMailNavigation = (direction) => {
//   const nextIndex = selectedMailIndex + direction;
//   const nextMail = filteredMailItems[nextIndex];
//   if (!nextMail) return;

//   setSelectedMail(nextMail);
//   setMailSendMessage(null);
// };
// const mailFolderLabel = mailFolder === "outbox" ? "outbox" : "sent items";
// const getOutboxCount = () => {
//   return mailItems.filter((mail) => Number(mail.status) === 0).length;
// };

// useEffect(() => {
//   if (!showLogoutMailConfirm) return;

//   const handleEsc = (event) => {
//     if (event.key === "Escape") {
//       setShowLogoutMailConfirm(false);

//       // Redirect to Main Page
//       navigate("/explorer"); // change route if needed
//     }
//   };

//   window.addEventListener("keydown", handleEsc);

//   return () => {
//     window.removeEventListener("keydown", handleEsc);
//   };
// }, [showLogoutMailConfirm, navigate]);
// const handleLogout = () => {
//   const outboxCount = mailItems.filter((mail) => Number(mail.status) === 0).length;

//   if (outboxCount > 0) {
//     setLogoutOutboxCount(outboxCount);
//     setShowLogoutMailConfirm(true);
//     setIsPopupOpen(false);
//     return;
//   }

//   proceedLogout();
// };


// const proceedLogout = async () => {
//   const logId = sessionStorage.getItem("LogId");
//   const userId = sessionStorage.getItem("userId");
//   const currentAuthToken = sessionStorage.getItem("CurrentAuthToken");

//   try {
//     if (!logId || !userId) {
//       alert("Logout failed: session expired");
//       return;
//     }

//     await api.LogoutUser(logId, userId, currentAuthToken);
//   } catch (error) {
//     console.error("Logout error:", error);
//   } finally {
//     AUTH_STORAGE_KEYS.forEach((key) => {
//       sessionStorage.removeItem(key);
//       localStorage.removeItem(key);
//     });
//     navigate("/", { replace: true });
//   }
// };

//   const handleResetPassword = async () => {
//     if (resetStep === 1) {
//       // Step 1: Request reset password
//       if (!emailId || !newPassword) {
//         setResetMessage({ text: "Please enter email and new password", type: "error" });
//         return;
//       }
//       setResetLoading(true);
//       try {
//         const userName = sessionStorage.getItem("userName");
//         const result = await api.RequestResetPassword({ userName, emailId, newPassword });
//         setResetMessage({ text: result.message || "OTP sent to your email", type: "success" });
//         setResetStep(2);
//       } catch (err) {
//         setResetMessage({ text: "Request failed: " + err.message, type: "error" });
//       }
//       setResetLoading(false);
//     } else {
//       // Step 2: Confirm with OTP
//       if (!otp) {
//         setResetMessage({ text: "Please enter the OTP", type: "error" });
//         return;
//       }
//       setResetLoading(true);
//       try {
//         const userName = sessionStorage.getItem("userName");
//         const result = await api.ConfirmResetPassword({ resetOtp: otp, userName });
//         const message = typeof result === 'string' ? result : (result.message || "Password updated successfully");
//         if (message.includes("Invalid or expired OTP")) {
//           setResetMessage({ text: message, type: "error" });
//         } else {
//           setResetMessage({ text: message, type: "success" });
//           setTimeout(() => {
//             setIsResetModalOpen(false);
//             setEmailId("");
//             setNewPassword("");
//             setOtp("");
//             setResetMessage(null);
//             setResetStep(1);
//           }, 2000);
//         }
//       } catch (err) {
//         setResetMessage({ text: "Confirmation failed: " + err.message, type: "error" });
//       }
//       setResetLoading(false);
//     }
//   };

//   const formatDateTime = (date) => {
//     return date.toLocaleString('en-IN', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit',
//       second: '2-digit',
//       hour12: true
//     });
//   };

//   const formatMailDateTime = (date) => {
//     if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
//     return date.toLocaleString('en-IN', {
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit',
//       hour12: true
//     });
//   };


// return (
//   <>
//     {/* ================= HEADER ================= */}
//     <div
//       className={`w-full fixed top-0 left-0 z-30 backdrop-blur-md shadow-md
//       py-2 px-4 flex items-center justify-between rounded-lg transition-all duration-300
//       ${scrolled ? "bg-base-100/40" : "bg-base-100/80"}`}
//     >

//     {/* LEFT SECTION — Toggle + Logo + Company Name */}
//     <div className="flex items-center gap-3">
//       {onToggleSidebar && (
//         <button
//           onClick={onToggleSidebar}
//           className="p-2 rounded-lg bg-base-300 hover:bg-base-400 text-base-content"
//         >
//           ☰
//         </button>
//       )}
//       <img
//   src={companyDetails?.logoUrl}
//   alt="Company Logo"
//   className="object-cover rounded-lg shadow-sm w-13 h-9 sm:w-17 sm:h-11"
//  />


//       {companyDetails?.companyDetails?.Table?.[0] && (
//         <h1 className="hidden text-lg font-bold tracking-wide sm:block md:text-xl text-base-content">
//           {companyDetails.companyDetails.Table[0].ComName}
//         </h1>
//       )}
//     </div>

//     {/* CENTER SECTION — Page actions */}
//     <div className="items-center justify-end flex-1 hidden px-4 sm:flex">
//       {headerActions}
//     </div>
//  <div className="relative flex items-start gap-3 mr-3">

//   {/* ✅ APPROVAL BUTTON */}
//   {permissions.pendingApprovals === 1 && (
//   <div className="header-action">
//     <button
//   onClick={navigateToApproval}
//   className="top-icon-button"
//   title="Pending Approvals"
//   aria-label="Pending Approvals"
// >
//   <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//     <path strokeLinecap="round" strokeLinejoin="round"
//       d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" />
//     <path strokeLinecap="round" strokeLinejoin="round"
//       d="M9 14l2 2 4-4" />
//   </svg>

//   {approvalCount > 0 && (
//     <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full animate-bounce">
//       {approvalCount}
//     </span>
//   )}
// </button>
//   <span className="hidden top-icon-label sm:block">
//     Pending Approvals
//   </span>
//   </div>
//   )}


//   {/* 🔔 NOTIFICATION BUTTON */}
//   {permissions.noticeboard === 1 && (
//   <div className="header-action">
//     <button
//       onClick={() => setShowNotice(!showNotice)}
//       className="top-icon-button"
//       aria-label="Toggle noticeboard"
//       title="Noticeboard"
//     >
//       <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//         <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2a2 2 0 01-.6 1.4L4 17h5" />
//         <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a3 3 0 006 0" />
//       </svg>

//       {taskCount > 0 && (
//         <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full animate-bounce">
//           {taskCount}
//         </span>
//       )}
//     </button>
//     <span className="hidden top-icon-label sm:block">
//       Noticeboard
//     </span>

//     {showNotice && (
//     <div
//   className="absolute right-0 z-50 mt-2 mx-2 flex w-[900px] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:mx-0"
//   style={{ height: "calc(100vh - 80px)", maxHeight: "calc(100vh - 80px)" }}
// >
//        <div className="flex items-center gap-4 px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
//   <div className="min-w-[170px]">
//     <h3 className="font-semibold">Noticeboard</h3>
//     <div className="text-xs text-blue-100">Tasks by due date</div>
//   </div>
  

//   <div className="relative flex-1 max-w-[420px]">
//     <input
//       type="search"
//       value={noticeSearch}
//       onChange={(e) => setNoticeSearch(e.target.value)}
//       placeholder="Search tasks..."
//       className="w-full pr-3 text-sm font-medium border outline-none h-9 rounded-xl border-white/30 bg-white/95 pl-9 text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-white/40"
//     />
//     <svg
//       className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400"
//       fill="none"
//       stroke="currentColor"
//       strokeWidth="2"
//       viewBox="0 0 24 24"
//     >
//       <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
//     </svg>
//   </div>

//   <button
//     onClick={() => setShowNotice(false)}
//     className="flex items-center justify-center w-8 h-8 ml-auto text-white transition rounded-full hover:bg-white/15"
//     aria-label="Close noticeboard"
//   >
//     x
//   </button>
// </div>

//         <div className="grid min-h-0 flex-1 grid-cols-[450px_450px] overflow-hidden bg-slate-50">
//           <div className="flex flex-col min-h-0 bg-white border-r border-slate-200">
//             <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
             
//               <div>
//                 <div className="text-sm font-semibold text-slate-800">
//                   {noticeSelectedDateKey === noticeTodayKey ? "Today" : noticeSelectedDateLabel}
//                 </div>
//                 <div className="text-xs text-slate-500">
//                   {filteredNoticeDateTasks.length} {filteredNoticeDateTasks.length === 1 ? "task" : "tasks"}
//                 </div>
//               </div>
//               <button
//                 type="button"
//                 onClick={goToNoticeToday}
//                 className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
//               >
//                 Today
//               </button>
//             </div>

//             <div className="h-[180px] shrink-0 space-y-2 overflow-y-auto p-3">
//               {filteredNoticeDateTasks.map((task) => (
//                 <div
//                   key={task.TaskId}
//                   className={`cursor-pointer rounded-xl border p-3 shadow-sm transition ${
//                     task.IsRead
//                       ? "border-emerald-100 bg-emerald-50/70 hover:bg-emerald-50"
//                       : "border-red-100 bg-red-50/70 hover:bg-red-50"
//                   }`}
//                   onClick={() => toggleTask(task.TaskId)}
//                 >
//                   <div className="flex items-start justify-between gap-3">
//                     <div className="min-w-0">
//                       <div className={`truncate text-sm font-semibold ${
//                         task.IsRead ? "text-emerald-800" : "text-red-800"
//                       }`}>
//                         {task.Title}
//                       </div>
//                       <div className="mt-1 text-xs line-clamp-2 text-slate-600">
//                         {task.Description}
//                       </div>
//                     </div>
//                     {!task.IsRead && (
//                       <span className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">
//                         New
//                       </span>
//                     )}
//                   </div>
//                   <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-slate-500">
//                     <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-700">
//                       {task.Priority || "-"}
//                     </span>
//                     <span>{task.Status || "-"}</span>
//                     <span className="ml-auto font-medium">By - {task.UserName || "-"}</span>
//                   </div>
//                 </div>
//               ))}

//              {filteredNoticeDateTasks.length === 0 && (
//                 <div className="flex items-center justify-center h-full min-h-0 p-4 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
//                   No tasks for this date
//                 </div>
//               )}
//             </div>

//             <div className="flex flex-col flex-1 min-h-0 p-2 border-t border-slate-200 bg-slate-50">
//               <div className="mb-2 grid grid-cols-[1fr_1fr_auto] items-end gap-2 rounded-xl border border-slate-200 bg-white p-2">
//                 <label className="min-w-0">
//                   <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">From Date</span>
//                   <input
//                     type="date"
//                     value={noticeFromDate}
//                     onChange={(event) => setNoticeFromDate(event.target.value)}
//                     className="w-full h-8 px-2 text-xs transition bg-white border rounded-lg outline-none border-slate-300 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
//                   />
//                 </label>
//                 <label className="min-w-0">
//                   <span className="mb-1 block text-[10px] font-bold uppercase text-slate-500">To Date</span>
//                   <input
//                     type="date"
//                     value={noticeToDate}
//                     onChange={(event) => setNoticeToDate(event.target.value)}
//                     className="w-full h-8 px-2 text-xs transition bg-white border rounded-lg outline-none border-slate-300 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
//                   />
//                 </label>
//                 <button
//                   type="button"
//                   onClick={applyNoticeDateRange}
//                   className="flex items-center justify-center h-8 px-3 text-xs font-semibold text-white transition bg-blue-600 rounded-lg hover:bg-blue-700"
//                   title="Refresh tasks"
//                   aria-label="Refresh tasks"
//                 >
//                   Refresh
//                 </button>
//               </div>

//               <div className="flex items-center justify-between mb-1">
//                 <div>
//                   <div className="text-sm font-semibold text-slate-800">Tasks By Date Range</div>
//                   <div className="text-xs text-slate-500">
//                     {filteredNoticeRangeTasks.length} {filteredNoticeRangeTasks.length === 1 ? "task" : "tasks"}
//                   </div>
//                 </div>
//                 <div className="text-[10px] font-semibold text-slate-600">
//                   {appliedNoticeFromDate} to {appliedNoticeToDate}
//                 </div>
//               </div>

//              <div className="flex-1 min-h-0 pr-2 overflow-y-auto scrollbar-thin">
//                 <div className="space-y-1">
//                   {filteredNoticeRangeTasks.map((task) => (
//                     <button
//                       key={task.TaskId}
//                       type="button"
//                       onClick={() => toggleTask(task.TaskId)}
//                       className={`w-full rounded-xl border px-3 py-2 text-left text-sm shadow-sm transition ${
//                         task.IsRead
//                           ? "border-emerald-100 bg-emerald-50/70 hover:bg-emerald-50"
//                           : "border-red-100 bg-red-50/70 hover:bg-red-50"
//                       }`}
//                       title={`By : ${task.UserName || "-"}`}
// >
//                       <div className="grid grid-cols-[38px_1fr_auto] items-center gap-3">
//                         <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-[11px] font-semibold text-slate-700">
//                           {new Date(task.Due_date || task.Start_date).getDate()}
//                         </div>
//                         <div>
//                           <div className="font-semibold truncate text-slate-800">{task.Title}</div>
//                           <div className="mt-0.5 text-[11px] leading-tight text-slate-500 line-clamp-1">{task.Description}</div>
//                         </div>
//                         <div className="flex flex-col items-end gap-1 text-[10px] text-slate-500">
                         
//                           <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">{task.Priority || "-"}</span>
//                           <span>{task.Status || "-"}</span>
//                         </div>
//                       </div>
//                     </button>
//                   ))}

//                   {filteredNoticeRangeTasks.length === 0 && (
//                     <div className="p-4 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
//                       No tasks found between selected dates
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="p-4 bg-slate-50">
//             <div className="flex items-center justify-between mb-4">
//               <button
//                 type="button"
//                 onClick={() => changeNoticeMonth(-1)}
//                 className="flex items-center justify-center w-8 h-8 transition rounded-full text-slate-600 hover:bg-white hover:text-blue-700"
//                 aria-label="Previous month"
//               >
//                 &lt;
//               </button>
//               <div className="text-sm font-bold text-slate-800">{noticeMonthLabel}</div>
//               <button
//                 type="button"
//                 onClick={() => changeNoticeMonth(1)}
//                 className="flex items-center justify-center w-8 h-8 transition rounded-full text-slate-600 hover:bg-white hover:text-blue-700"
//                 aria-label="Next month"
//               >
//                 &gt;
//               </button>
//             </div>

//             <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400">
//               {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
//                 <div key={day} className="py-1">{day}</div>
//               ))}
//             </div>
//             <div className="grid grid-cols-7 gap-1 mt-1">
//               {noticeCalendarDays.map((date, index) => {
//                 const key = date ? getDateKey(date) : `empty-${index}`;
//                 const count = date ? noticeTaskCountByDate[getDateKey(date)] || 0 : 0;
//                 const isSelected = date && getDateKey(date) === noticeSelectedDateKey;
//                 const isToday = date && getDateKey(date) === noticeTodayKey;

//                 return date ? (
//                   <button
//                     key={key}
//                     type="button"
//                     onClick={() => setNoticeSelectedDate(date)}
//                     aria-label={`${date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}${count ? `, ${count} ${count === 1 ? "task" : "tasks"}` : ", no tasks"}`}
//                     className={`relative flex h-10 flex-col items-center justify-center rounded-xl border text-xs font-semibold shadow-sm transition ${
//                       isSelected
//                         ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
//                         : isToday
//                           ? "border-blue-200 bg-blue-100 text-blue-700 hover:bg-blue-200"
//                           : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
//                     }`}
//                   >
//                     <span>{date.getDate()}</span>
//                     {count > 0 && (
//                       <span className={`mt-1 inline-flex min-w-[1.6rem] items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
//                         isSelected
//                           ? "bg-white text-blue-700"
//                           : "bg-emerald-500 text-white"
//                       }`}>
//                         {count}
//                       </span>
//                     )}
//                   </button>
//                 ) : (
//                   <div key={key} className="h-10" />
//                 );
//               })}
//             </div>

//             <div className="p-3 mt-4 bg-white border rounded-xl border-slate-200">
//               <div className="text-xs font-semibold text-slate-700">Month Tasks</div>
//               <div className="mt-1 text-2xl font-bold text-blue-700">{taskList.length}</div>
//               <div className="text-xs text-slate-400">Total visible noticeboard tasks</div>
//             </div>
//           </div>
//         </div>
//       </div>
//     )}

//     {false && showNotice && (
//       <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border shadow-2xl w-96 rounded-2xl border-base-200">

//             <div className="flex items-center justify-between px-4 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
//               <h3 className="font-semibold">Noticeboard</h3>
//               <button onClick={() => setShowNotice(false)}>✕</button>
//             </div>

//             <div className="p-3 space-y-3 overflow-y-auto max-h-80">
//               {paginatedTaskList.map((task) => (
//                 <div
//                   key={task.TaskId}
//               className={`p-3 border rounded-lg cursor-pointer transition duration-200 ${
//                 task.IsRead
//                   ? 'bg-green-50 border-green-300 hover:bg-green-100'
//                   : 'bg-red-50 border-red-300 hover:bg-red-100'
//               }`}
//               onClick={() => toggleTask(task.TaskId)}
//             >
//               <div className="flex items-center justify-between text-sm font-semibold">
//                <div>
//   <div className={`font-semibold ${
//     task.IsRead ? 'text-green-700' : 'text-red-700'
//   }`}>
//     {task.Title}
//   </div>

//   <div className="flex items-center justify-between w-full gap-12 mt-1 text-xs text-gray-500">
  
//   {/* LEFT */}
//   <span className="truncate max-w-[60%]">
//     By - {task.UserName}
//   </span>

//   {/* RIGHT */}
//   <span className="ml-auto text-right whitespace-nowrap">
//     {new Date(task.Due_date).toLocaleDateString("en-GB")}
//   </span>

// </div>
// </div>

//                 <div className="flex items-center gap-2">
//                   {!task.IsRead && (
//                     <span className="text-xs px-2 py-0.5 bg-red-500 text-white rounded">New</span>
//                   )}

//                   <span>{openTaskId === task.TaskId ? '▲' : '▼'}</span>
//                 </div>
//               </div>

//             </div>
//           ))}
//         </div>
//         {paginationNeeded && (
//           <div className="flex items-center justify-between px-4 py-3 border-t">
//             <button
//               onClick={() => goToPage(currentPage - 1)}
//               disabled={currentPage === 1}
//               className="px-2 py-1 text-xs font-semibold transition rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
//             >
//               &lt;
//             </button>
//             <div className="flex items-center gap-1">
//               {Array.from({ length: totalPages }).map((_, index) => {
//                 const page = index + 1;
//                 return (
//                   <button
//                     key={page}
//                     onClick={() => goToPage(page)}
//                     className={`px-2 py-1 text-xs font-semibold rounded-md transition ${
//                       page === currentPage
//                         ? 'bg-blue-600 text-white'
//                         : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
//                     }`}
//                   >
//                     {page}
//                   </button>
//                 );
//               })}
//             </div>
//             <button
//               onClick={() => goToPage(currentPage + 1)}
//               disabled={currentPage === totalPages}
//               className="px-2 py-1 text-xs font-semibold transition rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
//             >
//               &gt;
//             </button>
//           </div>
//         )}
//       </div>
//     )}
//   </div>
//   )}

//   {/* MAIL BUTTON */}
//   <div className="header-action">
//     <button
//       onClick={() => {
//         setMailFolder("outbox");
//         setSelectedMail(null);
//         setMailSendMessage(null);
//         setIsMailOpen(true);
//       }}
//       className="top-icon-button"
//       aria-label="Open mail"
//       title="Mail"
//     >
//       <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//         <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16v12H4z" />
//         <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 6 8-6" />
//       </svg>
//       <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-600 text-white rounded-full">
//         {mailItems.filter(x => x.status === 0).length}
//       </span>
//     </button>
//     <span className="hidden top-icon-label sm:block">
//       Mail
//     </span>
//   </div>

// <div className="relative flex items-center self-center gap-2 pl-3 ml-1 border-l border-base-content/10">
// <button
//     onClick={() => setIsPopupOpen(!isPopupOpen)}
//     className="flex items-center justify-center w-8 h-8 transition-colors rounded-full shadow-sm bg-base-100 text-base-content hover:bg-base-200"
//     title="User Menu"
//   >
//     <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
//       <path
//         fillRule="evenodd"
//         d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
//         clipRule="evenodd"
//       />
//     </svg>
//   </button>
  
//   <span
//     className="hidden sm:block text-sm font-medium text-base-content truncate max-w-[110px]"
//     title={userName}
//   >
//     {userName}
//   </span>

//   {isPopupOpen && (
//     <div
//       ref={popupRef}
//       className="absolute right-0 z-40 p-2 mt-2 border border-gray-300 rounded-lg shadow-xl bg-base-100 top-full dark:bg-gray-800 dark:border-gray-600 min-w-80"
//     >
//       <div className="flex items-center gap-2 px-3 py-2 mb-2 text-sm text-gray-900 border-b border-gray-300 dark:text-gray-100 dark:border-gray-600">
//         <svg className="w-4 h-4 text-gray-900 dark:text-gray-100" fill="currentColor" viewBox="0 0 20 20">
//           <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
//         </svg>
//         Login as {userName}
//       </div>
//       {permissions.resetPassword === 1 && (
//         <button
//           onClick={() => {
//             setIsResetModalOpen(true);
//             setIsPopupOpen(false);
//           }}
//           className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-blue-600 transition-colors rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:text-blue-400"
//         >
//           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
//             />
//           </svg>
//           Change Password
//         </button>
//       )}
//       {permissions.todoList === 1 && (
//         <button
//           onClick={() => {
//             /* Todo list */ setIsPopupOpen(false);
//           }}
//           className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
//         >
//           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
//             />
//           </svg>
//           Todo List
//         </button>
//       )}
//       {permissions.activityLog === 1 && (
//         <button
//           onClick={() => {
//             /* Activity Log */ setIsPopupOpen(false);
//           }}
//           className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
//         >
//           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path
//               strokeLinecap="round"
//               strokeLinejoin="round"
//               strokeWidth={2}
//               d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
//             />
//           </svg>
//           Activity Log
//         </button>
//       )}
//       <button
//         onClick={() => {
//           handleLogout();
//           setIsPopupOpen(false);
//         }}
//         className="flex items-center w-full gap-2 px-3 py-2 text-sm text-left text-gray-900 transition-colors rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-100"
//       >
//         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//           <path
//             strokeLinecap="round"
//             strokeLinejoin="round"
//             strokeWidth={2}
//             d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
//           />
//         </svg>
//         Logout
//       </button>
//     </div>
//   )}
// </div>
// </div>

//     {/* RIGHT SECTION — Time + Logout */}
//     <div className="flex items-center flex-shrink-0 gap-4">
//       {/* Time */}
//       <span className="text-xs font-medium whitespace-nowrap md:text-sm text-base-content/70">
//         {formatDateTime(currentTime)}
//       </span>



//     </div>

//     {/* Reset Password Modal */}
//     {isResetModalOpen && (
//       <>
//         <div className="fixed bottom-0 left-0 right-0 z-40 top-24 bg-black/40 backdrop-blur-md" onClick={() => setIsResetModalOpen(false)}></div>
//         <div className="fixed bottom-0 left-0 right-0 z-50 flex items-start justify-center p-4 top-24" onClick={() => setIsResetModalOpen(false)}>
//           <div onClick={(e) => e.stopPropagation()} className="bg-white border border-gray-300 rounded-xl p-6 
//                 w-full max-w-xl md:max-w-xl lg:max-w-xl
//                 max-h-[70vh] overflow-y-auto shadow-xl">
//                <h3 className="mb-4 text-xl font-semibold text-base-content">Change Password</h3>
//                {resetStep === 1 ? (
//                  <>
//                  <div className="mb-4">
//   <label className="block mb-1 text-sm font-medium text-base-content">
//     Username
//   </label>
//   <input
//     type="text"
//     value={userName}
//     disabled
//     className="w-full px-3 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed"
//   />
// </div>
//                    <div className="mb-4">
//                      <label className="block mb-1 text-sm font-medium text-base-content">Email ID</label>
//                      <input
//                        type="email"
//                        value={emailId}
//                        onChange={(e) => setEmailId(e.target.value)}
//                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
//                        placeholder="Enter your email"
//                      />
//                    </div>
//                    <div className="mb-4">
//                      <label className="block mb-1 text-sm font-medium text-base-content">New Password</label>
//                      <input
//                        type="password"
//                        value={newPassword}
//                        onChange={(e) => setNewPassword(e.target.value)}
//                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
//                        placeholder="Enter new password"
//                      />
//                    </div>
//                  </>
//                ) : (
//                  <div className="mb-4">
//                    <label className="block mb-1 text-sm font-medium text-base-content">Enter OTP</label>
//                    <input
//                      type="text"
//                      value={otp}
//                      onChange={(e) => setOtp(e.target.value)}
//                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white/40 backdrop-blur"
//                      placeholder="Enter the OTP sent to your email"
//                    />
//                  </div>
//                )}
//             {resetMessage && (
//               <div className={`mb-4 p-2 rounded-md text-sm ${resetMessage.type === "error" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
//                 {resetMessage.text}
//               </div>
//             )}
//             <div className="flex justify-end gap-2">          
//               <button
//                 onClick={handleResetPassword}
//                 disabled={resetLoading}
//                 className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-green-500 border border-green-600 rounded-md hover:bg-green-600 hover:border-green-700 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
//               >
//                 {resetLoading ? (resetStep === 1 ? "Requesting..." : "Confirming...") : (resetStep === 1 ? "Request Change" : "Confirm Change")}
//               </button>
//                  <button
//                 onClick={() => {
//                   setIsResetModalOpen(false);
//                   setResetStep(1);
//                   setEmailId("");
//                   setNewPassword("");
//                   setOtp("");
//                   setResetMessage(null);
//                 }}
//                 className="px-6 py-2 text-sm font-medium text-white transition-all duration-200 transform bg-red-500 border border-red-600 rounded-md hover:bg-red-600 hover:border-red-700 hover:shadow-md hover:scale-105"
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       </>
//     )}
//   </div>

//   {isMailOpen && (
//     <div
//       className="mail-overlay"
//       onClick={() => {
//         setIsMailOpen(false);
//         setSelectedMail(null);
//       }}
//     >
//       <div
//         className={`mail-popup mail-popup-wide mail-size-${mailPopupSize} ${isMailListCollapsed ? "mail-list-collapsed" : ""} ${isMailDragging ? "mail-popup-dragging" : ""}`}
//         style={{ transform: `translate(${mailPopupOffset.x}px, ${mailPopupOffset.y}px)` }}
//         onClick={(e) => e.stopPropagation()}
//       >
//       <div className="mail-popup-header" onMouseDown={handleMailPopupDragStart}>
//   <div className="mail-popup-title">
//     <button
//       type="button"
//       className={`mail-list-toggle-btn ${isMailListCollapsed ? "mail-list-toggle-active" : ""}`}
//       onClick={() => setIsMailListCollapsed((prev) => !prev)}
//       title={isMailListCollapsed ? "Show mail list" : "Hide mail list"}
//       aria-label={isMailListCollapsed ? "Show mail list" : "Hide mail list"}
//     >
//       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//         {isMailListCollapsed ? (
//           <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
//         ) : (
//           <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
//         )}
//       </svg>
//     </button>

//     <div>
//       <h2>Mail</h2>
//       <span>{filteredMailItems.length} {mailFolderLabel} messages</span>
//     </div>

//     <div className="mail-title-actions">
//       <button
//         type="button"
//         className="mail-send-btn mail-send-all-btn"
//         onClick={handleSendAllMail}
//         disabled={mailSendingAll}
//       >
//         Send All
//       </button>

//       <button
//         type="button"
//         className="mail-send-btn mail-pass-btn"
//         onClick={handlePassSendAll}
//         disabled={!mailSendingAll}
//       >
//         Pause
//       </button>
//     </div>
//   </div>

//   <div className="mail-popup-tools">

//       <button
//     type="button"
//     className="mail-icon-btn mail-refresh-btn"
//     onClick={loadMailOutbox}
//     disabled={mailLoading}
//     title="Refresh mails"
//     aria-label="Refresh mails"
//   >
//     <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//       <path
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         d="M4 4v6h6M20 20v-6h-6M5 19a9 9 0 0014-3M19 5a9 9 0 00-14 3"
//       />
//     </svg>
//   </button>
//     <div className="mail-search">
//       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//         <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m1.35-5.15a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
//       </svg>

//       <input
//         type="text"
//         placeholder="Search mail"
//         value={mailSearch}
//         onChange={(e) => setMailSearch(e.target.value)}
//       />
//     </div>

//     <button
//       type="button"
//       className="mail-icon-btn mail-zoom-btn"
//       onClick={() => resizeMailPopup("out")}
//       disabled={mailPopupSize === "compact"}
//       title="Zoom out"
//     >
//       -
//     </button>

//     <button
//       type="button"
//       className="mail-icon-btn mail-zoom-btn"
//       onClick={() => resizeMailPopup("in")}
//       disabled={mailPopupSize === "large"}
//       title="Zoom in"
//     >
//       +
//     </button>

//     <button
//       type="button"
//       className="mail-send-btn"
//       onClick={handleSendMail}
//       disabled={mailSending || mailSendingAll}
//     >
//       {mailSending ? "Sending..." : "Send"}
//     </button>

//     <button
//       type="button"
//       className="mail-icon-btn"
//       onClick={() => {
//         setIsMailOpen(false);
//         setSelectedMail(null);
//         setMailSendMessage(null);
//       }}
//       title="Close"
//     >
//       ×
//     </button>
//   </div>
// </div>

//         <div className="mail-workspace">
//           <div className="mail-list-pane">
//             <div className="mail-tabs" role="tablist" aria-label="Mail folders">
//               <button
//                 type="button"
//                 className={mailFolder === "outbox" ? "active" : ""}
//                 onClick={() => {
//                   setMailFolder("outbox");
//                   setSelectedMail(null);
//                   setMailSendMessage(null);
//                 }}
//                 role="tab"
//                 aria-selected={mailFolder === "outbox"}
//               >
//                 Outbox
//               </button>
//                 <button
//                 type="button"
//                 className={mailFolder === "sent" ? "active" : ""}
//                 onClick={() => {
//                   setMailFolder("sent");
//                   setSelectedMail(null);
//                   setMailSendMessage(null);
//                 }}
//                 role="tab"
//                 aria-selected={mailFolder === "sent"}
//               >
//                 Sent Items
//               </button>
//             </div>
//             <div className="mail-list">
//               {mailLoading && (
//                 <div className="mail-empty">Loading mail...</div>
//               )}
//               {!mailLoading && mailError && (
//                 <div className="mail-empty">{mailError}</div>
//               )}
//               {!mailLoading && !mailError && filteredMailItems.map((mail) => (
//                 <div
//                   key={mail.id}
//                   className={`mail-row ${selectedMail?.id === mail.id ? "mail-row-active" : ""} ${mail.status === 0 ? "mail-row-status-green" : "mail-row-status-red"}`}
//                   onClick={() => {
//                     setSelectedMail(mail);
//                     setMailSendMessage(null);
//                   }}
//                   onKeyDown={(event) => {
//                     if (event.key === "Enter" || event.key === " ") {
//                       event.preventDefault();
//                       setSelectedMail(mail);
//                       setMailSendMessage(null);
//                     }
//                   }}
//                   role="button"
//                   tabIndex={0}
//                 >
//                   <span className="mail-sender">
//                     <strong>{mail.senderName}</strong>
//                     <small>To: {mail.to}</small>
//                   </span>
//                   <span className="mail-subject">{mail.subject}</span>
//                   <span className="mail-attachment">
//                     {mail.attachmentCount > 0 && (
//                       <span title={`${mail.attachmentCount} attachment${mail.attachmentCount > 1 ? "s" : ""}`}>
//                         {mail.attachmentCount}
//                       </span>
//                     )}
//                   </span>
//                   <span className="mail-time"><span className="mail-time-label">Edit Time</span> {formatMailDateTime(mail.generatedAt)}</span>
//                   <button
//                     type="button"
//                     className="mail-delete-btn"
//                     onClick={(event) => handleDeleteMail(mail, event)}
//                     disabled={mailDeletingLogId === mail.logId}
//                     title="Delete mail"
//                     aria-label="Delete mail"
//                   >
//                     {mailDeletingLogId === mail.logId ? (
//                       <span className="mail-delete-spinner" aria-hidden="true"></span>
//                     ) : (
//                       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16" />
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M6 7l1 13h10l1-13" />
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M9 7V4h6v3" />
//                       </svg>
//                     )}
//                   </button>
//                 </div>
//               ))}
//               {!mailLoading && !mailError && filteredMailItems.length === 0 && (
//                 <div className="mail-empty">No mail found</div>
//               )}
//             </div>
//           </div>

//           <div className="mail-detail-pane">
//             {mailSendMessage && (
//               <div className={`mail-send-message mail-detail-message ${mailSendMessage.type === "error" ? "error" : "success"}`}>
//                 {mailSendMessage.text}
//               </div>
//             )}
//             {selectedMail ? (
//               <>
//                 <div className="mail-read-header">
//                   <h2>{selectedMail.subject}</h2>
//                   <div className="mail-read-navigation" aria-label="Navigate messages">
//                     <button
//                       type="button"
//                       onClick={() => handleMailNavigation(-1)}
//                       disabled={!canOpenPreviousMail}
//                       title="Previous mail"
//                       aria-label="Previous mail"
//                     >
//                       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
//                       </svg>
//                     </button>
//                     <button
//                       type="button"
//                       onClick={() => handleMailNavigation(1)}
//                       disabled={!canOpenNextMail}
//                       title="Next mail"
//                       aria-label="Next mail"
//                     >
//                       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
//                       </svg>
//                     </button>
//                   </div>
//                   <span className="mail-read-generated"><strong>Generated:</strong> {formatDateTime(selectedMail.generatedAt)}</span>
//                 </div>
//                 <div className="mail-read-meta">
//                   <div className="mail-read-to-row">
//                     <strong>To:</strong>
//                     {isMailToEditing ? (
//                       <span className="mail-to-edit-wrap">
//                         <input
//                           type="email"
//                           className="mail-to-input"
//                           value={selectedMail.to}
//                           onChange={(e) => handleMailToEdit(e.target.value)}
//                           placeholder="To email address"
//                         />
//                         <button
//                           type="button"
//                           className="mail-vendor-address-btn"
//                           onClick={handleFetchVendorToAddress}
//                           disabled={vendorToLoading}
//                           title="Fetch vendor mail address"
//                           aria-label="Fetch vendor mail address"
//                         >
//                           {vendorToLoading ? (
//                             <span className="mail-vendor-spinner" aria-hidden="true"></span>
//                           ) : (
//                             <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                               <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 6 8-6" />
//                               <path strokeLinecap="round" strokeLinejoin="round" d="M5 19h14a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1z" />
//                               <path strokeLinecap="round" strokeLinejoin="round" d="M17 3v4h4" />
//                             </svg>
//                           )}
//                         </button>
//                       </span>
//                     ) : (
//                       <span>{selectedMail.to}</span>
//                     )}
//                     <button
//                       className={`mail-edit-icon-btn ${isMailToEditing ? "mail-edit-icon-btn-active" : ""}`}
//                       type="button"
//                       onClick={toggleMailToEditMode}
//                       title={isMailToEditing ? "Finish To address editing" : "Edit To address"}
//                       aria-label={isMailToEditing ? "Finish To address editing" : "Edit To address"}
//                     >
//                       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M16.86 4.49l2.65 2.65M5 19l4.2-.7L19.2 8.29a1.88 1.88 0 000-2.66l-.83-.83a1.88 1.88 0 00-2.66 0L5.7 14.8 5 19z" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mail-read-meta-right">
//                     <div><strong>From:</strong> {selectedMail.from}</div>
//                   </div>
//                 </div>
//                 <div className={`mail-read-scroll ${isMailListCollapsed && selectedMail.fileUrl ? "mail-read-split" : ""}`}>
//                   <div className="mail-content-column">
//                   <div className="mail-body-actionbar">
//                     <button
//                       className={`mail-edit-toggle ${isMailContentEditing ? "mail-edit-toggle-active" : ""}`}
//                       type="button"
//                       onClick={toggleMailContentEditMode}
//                       title={isMailContentEditing ? "Finish content editing" : "Edit mail content"}
//                       aria-label={isMailContentEditing ? "Finish content editing" : "Edit mail content"}
//                     >
//                       <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" d="M16.86 4.49l2.65 2.65M5 19l4.2-.7L19.2 8.29a1.88 1.88 0 000-2.66l-.83-.83a1.88 1.88 0 00-2.66 0L5.7 14.8 5 19z" />
//                       </svg>
//                       <span>{isMailContentEditing ? "Done" : "Edit"}</span>
//                     </button>
//                     <button
//                       className="mail-update-btn"
//                       type="button"
//                       onClick={handleUpdateMailContent}
//                       disabled={mailUpdating}
//                       title="Update mail content"
//                       aria-label="Update mail content"
//                     >
//                       {mailUpdating ? "Updating..." : "Update"}
//                     </button>
//                   </div>
//                   {isMailContentEditing && (
//                   <div className="mail-editor-toolbar">
//                     <select
//                       className="mail-editor-select mail-editor-format"
//                       defaultValue="p"
//                       onChange={(e) => handleMailFormatBlock(e.target.value)}
//                       title="Paragraph style"
//                       aria-label="Paragraph style"
//                     >
//                       <option value="p">Normal</option>
//                       <option value="h1">Heading 1</option>
//                       <option value="h2">Heading 2</option>
//                       <option value="h3">Heading 3</option>
//                       <option value="pre">Code</option>
//                     </select>
//                     <select
//                       className="mail-editor-select mail-editor-font"
//                       defaultValue="Arial"
//                       onChange={(e) => handleMailFormatCommand("fontName", e.target.value)}
//                       title="Font family"
//                       aria-label="Font family"
//                     >
//                       {MAIL_FONT_FAMILIES.map((font) => (
//                         <option key={font} value={font}>{font}</option>
//                       ))}
//                     </select>
//                     <select
//                       className="mail-editor-select mail-editor-size"
//                       defaultValue=""
//                       onChange={(e) => handleMailFormatCommand("fontSize", e.target.value)}
//                       title="Font size"
//                       aria-label="Font size"
//                     >
//                       <option value="" disabled>(Font Size)</option>
//                       {MAIL_FONT_SIZES.map((size) => (
//                         <option key={size.value} value={size.value}>{size.label}</option>
//                       ))}
//                     </select>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("bold")} title="Bold" aria-label="Bold">B</button>
//                     <button type="button" className="mail-editor-btn mail-editor-italic" onClick={() => handleMailFormatCommand("italic")} title="Italic" aria-label="Italic">I</button>
//                     <button type="button" className="mail-editor-btn mail-editor-underline" onClick={() => handleMailFormatCommand("underline")} title="Underline" aria-label="Underline">U</button>
//                     <button type="button" className="mail-editor-btn mail-editor-strike" onClick={() => handleMailFormatCommand("strikeThrough")} title="Strikethrough" aria-label="Strikethrough">ab</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyLeft")} title="Align left" aria-label="Align left">L</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyCenter")} title="Align center" aria-label="Align center">C</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyRight")} title="Align right" aria-label="Align right">R</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("justifyFull")} title="Justify" aria-label="Justify">J</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("insertUnorderedList")} title="Bullet list" aria-label="Bullet list">List</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("insertOrderedList")} title="Numbered list" aria-label="Numbered list">1.</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("outdent")} title="Decrease indent" aria-label="Decrease indent">&lt;</button>
//                     <button type="button" className="mail-editor-btn" onClick={() => handleMailFormatCommand("indent")} title="Increase indent" aria-label="Increase indent">&gt;</button>
//                     <button
//                       type="button"
//                       className="mail-editor-btn"
//                       onMouseDown={(e) => e.preventDefault()}
//                       onClick={handleMailCreateLink}
//                       disabled={mailLinkInserting}
//                       title="Insert module hyperlink"
//                       aria-label="Insert module hyperlink"
//                     >
//                       {mailLinkInserting ? "Inserting..." : "Insert"}
//                     </button>
//                     <label className="mail-editor-color" title="Text color" aria-label="Text color">
//                       <span>A</span>
//                       <input type="color" defaultValue={MAIL_TEXT_COLORS[0]} onChange={(e) => handleMailFormatCommand("foreColor", e.target.value)} />
//                     </label>
//                     <select
//                       className="mail-editor-select mail-editor-color-select"
//                       defaultValue=""
//                       onChange={(e) => handleMailFormatCommand("foreColor", e.target.value)}
//                       title="Text color presets"
//                       aria-label="Text color presets"
//                     >
//                       <option value="" disabled>Text Color</option>
//                       {MAIL_TEXT_COLORS.map((color) => (
//                         <option key={color} value={color}>{color}</option>
//                       ))}
//                     </select>
//                     <label className="mail-editor-color" title="Highlight color" aria-label="Highlight color">
//                       <span>H</span>
//                       <input type="color" defaultValue={MAIL_HIGHLIGHT_COLORS[1]} onChange={(e) => handleMailFormatCommand("hiliteColor", e.target.value)} />
//                     </label>
//                     <select
//                       className="mail-editor-select mail-editor-color-select"
//                       defaultValue=""
//                       onChange={(e) => handleMailFormatCommand("hiliteColor", e.target.value)}
//                       title="Highlight presets"
//                       aria-label="Highlight presets"
//                     >
//                       <option value="" disabled>Highlight</option>
//                       {MAIL_HIGHLIGHT_COLORS.map((color) => (
//                         <option key={color} value={color}>{color}</option>
//                       ))}
//                     </select>
//                     <button type="button" className="mail-editor-btn" onClick={handleMailClearFormat} title="Clear formatting" aria-label="Clear formatting">Tx</button>
//                   </div>
//                   )}
//                   <div
//                     key={selectedMail.id}
//                     ref={mailBodyEditorRef}
//                     className={`mail-read-body mail-rich-editor ${isMailContentEditing ? "mail-rich-editor-editing" : "mail-rich-editor-readonly"}`}
//                     contentEditable={isMailContentEditing}
//                     suppressContentEditableWarning
//                     onMouseUp={saveMailEditorSelection}
//                     onKeyUp={saveMailEditorSelection}
//                     onInput={(e) => {
//                       saveMailEditorSelection();
//                       handleMailBodyEdit(e.currentTarget.innerHTML);
//                     }}
//                   />
//                   </div>
//                   {selectedMail.fileUrl && (
//                     <div className="mail-inline-pdf">
//                       <div className="mail-inline-pdf-header">
//                         <strong>PDF Preview</strong>
//                         <span>{selectedMail.fileName || "Attachment.pdf"}</span>
//                       </div>
//                       <iframe
//                         className="mail-inline-pdf-frame"
//                         src={selectedMail.fileUrl}
//                         title={selectedMail.fileName || "PDF preview"}
//                       />
//                     </div>
//                   )}
//                 </div>
//               </>
//             ) : (
//               <div className="mail-detail-empty">
//                 <h3>Select a mail</h3>
//                 <p>Choose a message from the left list to review, edit content, preview PDF, and send.</p>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   )}

//   {selectedTask && (
//     <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
//       <div className="w-full max-w-5xl overflow-hidden bg-white shadow-2xl rounded-2xl animate-fadeIn">
//         {/* 🔷 HEADER */}
//         <div className="flex items-center justify-between px-5 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
//           <h3 className="text-lg font-semibold tracking-wide">{selectedTask.Title}</h3>
//           <button
//             onClick={() => setOpenTaskId(null)}
//             className="text-white transition hover:scale-110"
//           >
//             ✕
//           </button>
//         </div>

//         {/* 🔹 BODY */}
//         <div className="grid gap-4 p-5 lg:grid-cols-[0.72fr_1.28fr]">
//           {/* Description */}
//           <div className="order-2 p-3 border rounded-lg bg-gray-50 lg:col-start-2">
//             <div className="mb-1 text-sm font-semibold text-gray-600">Description</div>
//             <div className="text-sm text-gray-800">{selectedTask.Description}</div>
//           </div>

//           {/* Details Grid */}
//           <div className="grid order-3 grid-cols-2 gap-3 text-sm lg:col-start-2 xl:grid-cols-4">
//             <div className="p-2 border rounded-lg bg-blue-50">
//               <div className="text-xs text-gray-500">Priority</div>
//               <div className="font-semibold text-blue-700">{selectedTask.Priority}</div>
//             </div>
//             <div className="p-2 border rounded-lg bg-purple-50">
//               <div className="text-xs text-gray-500">Status</div>
//               <div className="font-semibold text-purple-700">{selectedTask.Status}</div>
//             </div>
//             <div className="p-2 border rounded-lg bg-green-50">
//               <div className="text-xs text-gray-500">Start Date</div>
//               <div className="font-semibold">
//                 {new Date(selectedTask.Start_date).toLocaleDateString("en-GB")}
//               </div>
//             </div>
//             <div className="p-2 border rounded-lg bg-red-50">
//               <div className="text-xs text-gray-500">Due Date</div>
//               <div className="font-semibold text-red-600">
//                 {new Date(selectedTask.Due_date).toLocaleDateString("en-GB")}
//               </div>
//             </div>
//           </div>

//           <div className="grid order-1 gap-4 lg:contents">
//           {/* 🔹 ERP LINKS TABLE */}
//           <div className="order-5 overflow-hidden border border-blue-100 shadow-sm rounded-xl bg-blue-50/40 lg:col-start-2">
//             <div className="px-4 py-3">
//               <h4 className="text-sm font-semibold text-blue-900">Linked Documents</h4>
//               <div className="text-xs text-blue-500">Related modules and document references</div>
//             </div>
//             <div className="mx-4 mb-4 overflow-hidden bg-white border border-blue-100 rounded-lg">
//               <div className="grid grid-cols-2 px-3 py-2 text-xs font-semibold text-white bg-blue-700">
//                 <div>Module</div>
//                 <div>Document</div>
//               </div>
//               <div className="overflow-y-auto text-sm max-h-64">
//                 {getLinksByTask(selectedTask.TaskId).map((link) => (
//                   <div
//                     key={link.LinkId}
//                     className="grid grid-cols-2 px-3 py-2 transition border-b border-blue-50 hover:bg-blue-50 gap-y-1"
//                   >
//                     <div>
//                       <div className="font-medium text-blue-700">{link.Module}</div>
//                       <div className="text-xs text-gray-500 truncate">{link.MdlId}</div>
//                     </div>
//                    <div className="text-gray-700 break-words whitespace-normal">
//   {link.Document}
// </div>
//                   </div>
//                 ))}
//                 {getLinksByTask(selectedTask.TaskId).length === 0 && (
//                   <div className="p-3 text-center text-gray-400">No linked documents</div>
//                 )}
//               </div>
//             </div>
//           </div>

//           <div className="order-1 overflow-visible border shadow-sm border-slate-200 rounded-xl bg-slate-50/80 lg:row-span-3">
//             <div className="flex items-center justify-between gap-3 px-4 py-3">
//               <div>
//                 <div className="text-sm font-semibold text-slate-700">Task Log</div>
//                 <div className="text-xs text-slate-500">Recent notes and status updates</div>
//               </div>
//               <button
//   type="button"
//   onClick={() => {
//     setShowTaskLogForm((value) => !value);
//     setTaskLogMessage(null);
//   }}
//   className="flex items-center justify-center text-xl font-semibold leading-none text-white transition bg-blue-600 rounded-full shadow-md h-9 w-9 shadow-blue-200 hover:bg-blue-700 hover:shadow-blue-300"
//   aria-label={showTaskLogForm ? "Hide task log form" : "Add task log"}
//   title={showTaskLogForm ? "Hide task log form" : "Add task log"}
// >
//   {showTaskLogForm ? "-" : "+"}
// </button>
//               {/* <button
//                 type="button"
//                 onClick={() => {
//                   if (selectedTaskLogs.length > 0) return;
//                   setShowTaskLogForm((value) => !value);
//                   setTaskLogMessage(null);
//                 }}
//                 disabled={selectedTaskLogs.length > 0}
//                 className="flex items-center justify-center text-xl font-semibold leading-none text-white transition bg-blue-600 rounded-full shadow-md h-9 w-9 shadow-blue-200 hover:bg-blue-700 hover:shadow-blue-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none"
//                 aria-label={selectedTaskLogs.length > 0 ? "Task log already exists" : showTaskLogForm ? "Hide task log form" : "Add task log"}
//                 title={selectedTaskLogs.length > 0 ? "Task log already exists" : showTaskLogForm ? "Hide task log form" : "Add task log"}
//               >
//                 {showTaskLogForm ? "-" : "+"}
//               </button> */}
//             </div>

//             <div className="px-4 py-4 bg-white border-t">
//               <div className="pr-1 space-y-3 overflow-y-auto max-h-48">
//                 {selectedTaskLogs.map((log) => (
//                   <div
//                     key={log.TlogId}
//                     className="p-3 transition bg-white border shadow-sm rounded-xl border-slate-200 hover:border-blue-200 hover:bg-blue-50/40"
//                   >
//                     <div className="flex items-start justify-between gap-3">
//                       <div className="flex-1 min-w-0">
//                         <div className="mb-2 text-sm font-semibold break-words text-slate-800">
//                           {log.Notes || "-"}
//                         </div>
//                         <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
//                           <span className="inline-flex rounded-full bg-purple-50 px-2 py-0.5 font-semibold text-purple-700">
//                             {log.Status || "-"}
//                           </span>
//                           <span>{formatTaskLogTime(log.UpdateTime)}</span>
//                           {/* <span className="font-semibold text-slate-600">{log.UpdateBy || "-"}</span> */}
//                         </div>
//                       </div>
//                       <button
//                         type="button"
//                         onClick={() => handleDeleteTaskLog(log)}
//                         disabled={taskLogDeletingId === log.TlogId}
//                         className="flex items-center justify-center w-8 h-8 text-red-600 transition border border-red-100 rounded-full shrink-0 bg-red-50 hover:border-red-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
//                         title="Delete task log"
//                         aria-label="Delete task log"
//                       >
//                         {taskLogDeletingId === log.TlogId ? (
//                           <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
//                         ) : (
//                           <svg
//                             className="w-4 h-4"
//                             viewBox="0 0 24 24"
//                             fill="none"
//                             stroke="currentColor"
//                             strokeWidth="2"
//                             strokeLinecap="round"
//                             strokeLinejoin="round"
//                             aria-hidden="true"
//                           >
//                             <path d="M3 6h18" />
//                             <path d="M8 6V4h8v2" />
//                             <path d="M19 6l-1 14H6L5 6" />
//                             <path d="M10 11v5" />
//                             <path d="M14 11v5" />
//                           </svg>
//                         )}
//                       </button>
//                     </div>
//                   </div>
//                 ))}
//                 {selectedTaskLogs.length === 0 && (
//                   <div className="px-3 py-5 text-sm text-center border border-dashed rounded-xl border-slate-300 bg-slate-50 text-slate-400">
//                     No task logs available
//                   </div>
//                 )}
//               </div>
//             </div>

//             {showTaskLogForm && (
//               <div className="px-4 py-4 bg-white border-t">
//                 <div className="p-4 border shadow-sm rounded-xl border-slate-200 bg-slate-50/70">
//                 <div className="grid gap-4">
//                   <label className="block">
//                     <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-slate-500">Notes</span>
//                     <textarea
//                       value={taskLogNotes}
//                       onChange={(event) => setTaskLogNotes(event.target.value)}
//                       rows={3}
//                       className="min-h-[88px] w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-inner outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
//                       placeholder="Type the task update..."
//                     />
//                   </label>
//                   <label className="block">
//                     <span className="block mb-2 text-xs font-bold tracking-wide uppercase text-slate-500">Status</span>
//                     <CustomDropdown
//                       options={taskStatusOptions.length > 0 ? taskStatusOptions : [{ Status: selectedTask.Status }]}
//                       valueKey="Status"
//                       labelKey="Status"
//                       value={taskLogStatus}
//                       onChange={setTaskLogStatus}
//                       placeholder="Select status"
//                       openDirection="up"
//                     />
//                   </label>
//                 </div>

//                 {taskLogMessage && (
//                   <div
//                     className={`mt-3 rounded-lg px-3 py-2 text-sm font-medium ${
//                       taskLogMessage.type === "success"
//                         ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
//                         : "border border-red-200 bg-red-50 text-red-700"
//                     }`}
//                   >
//                     {taskLogMessage.text}
//                   </div>
//                 )}

//                 <div className="flex justify-end mt-4">
//                   <button
//                     type="button"
//                     onClick={handleSaveTaskLog}
//                     disabled={taskLogSaving}
//                     className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
// >
//                     {taskLogSaving ? "Adding..." : "Add"}
//                   </button>
//                 </div>
//                 </div>
//               </div>
//             )}
//           </div>
//           </div>
//         </div>

//         {/* 🔻 FOOTER */}
//         <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
          
//        <button
//   onClick={() => handleMarkAsRead(selectedTask.TaskId)}
//   className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
// >
//   Read
// </button>
//         </div>
//       </div>
//     </div>
//   )}

//   {showLogoutMailConfirm && (
//   <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
//     <div className="w-[420px] max-w-[90vw] rounded-2xl bg-white shadow-2xl border border-blue-100 overflow-hidden">
//     <div className="flex items-center justify-between px-5 py-4 text-white bg-gradient-to-r from-blue-600 to-indigo-600">
//   <div>
//     <h3 className="text-lg font-bold">Pending Outbox Mails</h3>
//     <p className="text-sm text-blue-100">Before you logout</p>
//   </div>

//   <button
//     type="button"
//     className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/20"
//     title="Close (Esc)"
//     onClick={() => {
//       setShowLogoutMailConfirm(false);
//       proceedLogout();
//     }}
//   >
//     ✕
//   </button>
// </div>

//       <div className="p-5">
//         <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-blue-50">
//           <div className="flex items-center justify-center text-white bg-blue-600 rounded-full h-11 w-11">
//             ✉
//           </div>
//           <div>
//             <div className="font-bold text-slate-800">
//               {logoutOutboxCount} mail(s) in your outbox
//             </div>
//             <div className="text-sm text-slate-500">
//               Do you want to open mail popup and send them now?
//             </div>
//           </div>
//         </div>

//         <div className="flex justify-end gap-3">
//           <button
//             type="button"
//             className="px-5 py-2 text-sm font-bold border rounded-full border-slate-300 text-slate-600 hover:bg-slate-100"
//             onClick={() => {
//               setShowLogoutMailConfirm(false);
//               proceedLogout();
//             }}
//           >
//             No, Logout
//           </button>

//           <button
//             type="button"
//             className="px-5 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-lg hover:bg-blue-700"
//             onClick={() => {
//               setShowLogoutMailConfirm(false);
//               setMailFolder("outbox");
//               setSelectedMail(null);
//               setMailSendMessage(null);
//               setIsMailOpen(true);
//             }}
//           >
//             Yes, Open Mail
//           </button>
//         </div>
//       </div>
//     </div>
//   </div>
// )}

//   </>
// );
// }
